import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { Database, MagnifyingGlass, SignOut, User, ShieldCheck, CaretDown, CaretUp, PencilSimple, Spinner, Funnel, ArrowLeft, X, Phone, Envelope, MapPin, CalendarBlank, FileText, Receipt, File as FileIcon, VideoCamera, ChatCircle, BookOpen, PaperPlaneTilt, TreeStructure, Key, Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import api, { checkDbStatus, fetchCustomers, fetchCustomerSummary, fetchQuote, updateQuoteHeading, addQuoteLine, updateQuoteLine, deleteQuoteLine, searchParts,
} from "@/lib/api";
import {
  useCustomerPersons, useCustomerQuotes, useCustomerCompressors,
  useCustomerPartsPurchased, useCustomerJournal, useCustomerNotes,
  useCustomerPhoneLogs,
} from "@/lib/queryHooks";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import CompressorDetailModal from "@/components/CompressorDetailModal";
import CompressorPmViewerModal from "@/components/CompressorPmViewerModal";
import AddCustomerModal from "@/components/AddCustomerModal";
import VideoCallModal from "@/components/VideoCallModal";
import SmsChatPanel from "@/components/SmsChatPanel";
import SendPublicFilesModal from "@/components/SendPublicFilesModal";
import { customerTypeIcon } from "@/components/CustomerTypeIcons";
import { NavLink, useNavigate } from "react-router-dom";
import WavingFlag from "@/components/WavingFlag";
import RecordButton from "@/components/RecordButton";
import CustomerRecordingsSection from "@/components/CustomerRecordingsSection";
import AdminPanel from "@/components/AdminPanel";
import RotorIcon from "@/components/RotorIcon";
import PartFilesModal from "@/components/PartFilesModal";
import WarrantyClaimPickerModal from "@/components/WarrantyClaimPickerModal";
import AIProvenanceBadge from "@/components/AIProvenanceBadge";
import PinGeneratorDialog from "@/components/PinGeneratorDialog";
import HeaderBrand from "@/components/HeaderBrand";
import CustomerDetailTour, { TourHelpButton } from "@/components/CustomerDetailTour";
import { useT } from "@/lib/i18n";

const COUNTRY_COLOR = { US: "#1D4ED8", CA: "#D80621", Other: "#475569" };

const COUNTRY_CONFIG = {
  US: { label: "US Customers", flag: "https://flagcdn.com/w320/us.png", accent: "text-blue-700", accentBg: "bg-blue-50", accentBorder: "border-blue-100", groupText: "text-blue-900", badge: "text-blue-500 bg-blue-100", hoverRow: "hover:bg-blue-50/70", oddBg: "bg-blue-50/40", oddSticky: "bg-blue-50", tabActive: "border-blue-700 text-blue-700", detailAccent: "bg-blue-700", cardBorder: "border-blue-200" },
  CA: { label: "Canadian Customers", flag: "https://flagcdn.com/w320/ca.png", accent: "text-[#D80621]", accentBg: "bg-red-50", accentBorder: "border-red-100", groupText: "text-red-900", badge: "text-red-600 bg-red-100", hoverRow: "hover:bg-red-50/50", oddBg: "bg-red-50/50", oddSticky: "bg-red-50", tabActive: "border-[#D80621] text-[#D80621]", detailAccent: "bg-[#D80621]", cardBorder: "border-red-200" },
  Other: { label: "Other Countries", flag: null, accent: "text-slate-700", accentBg: "bg-slate-50", accentBorder: "border-slate-200", groupText: "text-slate-900", badge: "text-slate-500 bg-slate-200", hoverRow: "hover:bg-slate-50/50", oddBg: "bg-slate-100/70", oddSticky: "bg-slate-100", tabActive: "border-slate-700 text-slate-700", detailAccent: "bg-slate-700", cardBorder: "border-slate-300" },
};

const COLUMNS = [
  { key: "Company Name", label: "Company Name", defW: 220, align: "left", sticky: true, filterType: "text" },
  { key: "Type", label: "Type", defW: 110, align: "left", filterType: "text" },
  // req #12 (iter110): Avg Quote / Credit / Debit / Currency are derived
  // financial roll-ups stored across multiple tables and not directly
  // sortable/searchable on the MySQL side without expensive joins. Per
  // the customer's request, leave them as read-only display columns —
  // no sort header, no filter input.
  { key: "avg_quote", label: "Avg Quote", defW: 110, align: "right", format: "money", sortable: false, filterType: null },
  { key: "Credit", label: "Credit", defW: 90, align: "right", format: "money", sortable: false, filterType: null },
  { key: "Debit", label: "Debit", defW: 90, align: "right", format: "money", sortable: false, filterType: null },
  { key: "Currency", label: "Currency", defW: 75, align: "left", sortable: false, filterType: null },
  { key: "Answered By", label: "Answered By", defW: 160, align: "left", filterType: "text" },
  { key: "Bill to Address", label: "Bill to Address", defW: 280, align: "left", filterType: "text" },
  { key: "Segment", label: "Segment", defW: 150, align: "left", filterType: "text" },
  { key: "Contact Date", label: "Contact Date", defW: 130, align: "left", filterType: null, formatDate: true },
  { key: "Person(1)", label: "Contact", defW: 140, align: "left", filterType: "text" },
  { key: "Email(1)", label: "Email", defW: 180, align: "left", filterType: "text" },
  { key: "TelePhone(1)", label: "Phone", defW: 120, align: "left", filterType: null },
  { key: "Compressor_Owner", label: "Compressor", defW: 100, align: "left", filterType: "text" },
  { key: "_Notes", label: "Notes", defW: 200, align: "left", filterType: null },
];

const SUPER_EDITOR_EMAILS = ["bora@usaircompressor.com", "deniz@usaircompressor.com", "jan@usaircompressor.com"];

function formatCell(value, format, col) {
  if (value == null || value === "" || value === "None") return "-";
  if (format === "money" && !isNaN(value)) return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  if (col?.formatDate && value) {
    try { return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return String(value); }
  }
  const str = String(value);
  return str.length > 100 ? str.slice(0, 100) + "..." : str;
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function getDayOfWeek(d) { if (!d) return ""; try { return DAY_NAMES[new Date(d).getDay()] || ""; } catch { return ""; } }
// Sort priority within a week: Friday first, then Thu, Wed, Tue, Mon, then weekend last.
// JS getDay(): Sun=0 Mon=1 Tue=2 Wed=3 Thu=4 Fri=5 Sat=6
const DAY_SORT_RANK = { 5: 0, 4: 1, 3: 2, 2: 3, 1: 4, 6: 5, 0: 6 };
function getDaySortRank(d) {
  if (!d) return 99;
  try { return DAY_SORT_RANK[new Date(d).getDay()] ?? 99; } catch { return 99; }
}
const DAY_BADGE_STYLE = {
  Fri: "bg-amber-100 text-amber-800 border-amber-200",
  Thu: "bg-sky-100 text-sky-800 border-sky-200",
  Wed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Tue: "bg-indigo-100 text-indigo-800 border-indigo-200",
  Mon: "bg-slate-200 text-slate-700 border-slate-300",
  Sat: "bg-rose-100 text-rose-700 border-rose-200",
  Sun: "bg-rose-100 text-rose-700 border-rose-200",
};

// Normalize a phone number to an E.164-ish tel: URI so iOS/Android reliably dial.
// - Keeps leading `+` if present
// - Prepends `+1` for 10-digit North American numbers
// - Converts leading `1` + 10 digits to `+1...`
// - Appends extension via `,` pause DTMF (iOS) only when provided
function toTelHref(raw, ext) {
  if (!raw) return null;
  const cleaned = String(raw).replace(/[^+0-9]/g, "");
  let num;
  if (cleaned.startsWith("+")) num = cleaned;
  else if (cleaned.length === 10) num = `+1${cleaned}`;
  else if (cleaned.length === 11 && cleaned.startsWith("1")) num = `+${cleaned}`;
  else num = cleaned;
  const extPart = ext ? `,${String(ext).replace(/[^0-9]/g, "")}` : "";
  return `tel:${num}${extPart}`;
}

function formatYearWeek(yw) {
  if (!yw || yw === 0) return "No Contact Date";
  const s = String(yw);
  if (s.length < 5) return `Week ${s}`;
  const year = parseInt(s.slice(0, 4), 10);
  const week = parseInt(s.slice(4), 10);
  const jan4 = new Date(year, 0, 4);
  const dow = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dow + 1 + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${year} — Week ${week}  (${fmt(monday)} – ${fmt(sunday)})`;
}

export default function CustomerPage({ user, onLogout, country }) {
  const cfg = COUNTRY_CONFIG[country] || COUNTRY_CONFIG.US;
  const navigate = useNavigate();
  const t = useT();
  const [dbConnected, setDbConnected] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [filters, setFilters] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // ─── Recently-viewed customers (per-user, persisted in localStorage) ───
  // Capped at 5; newest first; updated whenever a detail panel opens.
  const RECENT_KEY = `usair_recent_customers_${(user?.email || "anon").toLowerCase()}`;
  const RECENT_MAX = 5;
  const [recentCustomers, setRecentCustomers] = useState(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      return raw ? JSON.parse(raw).slice(0, RECENT_MAX) : [];
    } catch { return []; }
  });
  const pushRecentCustomer = useCallback((c) => {
    if (!c?.CustomerID) return;
    const entry = {
      CustomerID: c.CustomerID,
      "Company Name": c["Company Name"] || "",
      Type: c.Type || "",
      addr_country: c.addr_country || "",
      ts: Date.now(),
    };
    setRecentCustomers((prev) => {
      const filtered = (prev || []).filter((p) => p.CustomerID !== entry.CustomerID);
      const next = [entry, ...filtered].slice(0, RECENT_MAX);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* quota exceeded — ignore */ }
      return next;
    });
  }, [RECENT_KEY]);
  const removeRecentCustomer = useCallback((cid) => {
    setRecentCustomers((prev) => {
      const next = (prev || []).filter((p) => p.CustomerID !== cid);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, [RECENT_KEY]);
  const openCustomer = useCallback((c) => {
    setSelectedCustomer(c);
    pushRecentCustomer(c);
  }, [pushRecentCustomer]);

  // Deep-link support (iter111 — req #1): the Compressor Detail modal's
  // owner-company link routes to this page with `?focus=<CustomerID>`.
  // Once the customer list has loaded, find the matching row and open
  // its detail panel automatically. Removes the param afterwards so a
  // back-button from inside the modal doesn't re-trigger.
  const focusedRef = useRef(false);
  useEffect(() => {
    if (focusedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const focus = params.get("focus");
    if (!focus) return;
    if (loading) return;
    focusedRef.current = true;
    const list = data?.items || [];
    const match = list.find((c) => c.CustomerID === focus);
    if (match) {
      openCustomer(match);
    } else {
      // Couldn't find it on the current page (might be on a different
      // country segment or filtered out). Open with a stub so the
      // detail modal still loads — its sub-fetches use CustomerID only.
      openCustomer({ CustomerID: focus, "Company Name": "" });
    }
    params.delete("focus");
    const newQs = params.toString();
    window.history.replaceState({}, "",
      window.location.pathname + (newQs ? `?${newQs}` : ""));
  }, [loading, data, openCustomer]);

  useEffect(() => { checkDbStatus().then((d) => setDbConnected(d.connected)).catch(() => setDbConnected(false)); }, []);
  // Each load() call gets a monotonically increasing request id. When a
  // response comes back we ignore it unless its id is still the latest one
  // we asked for. This kills the rapid-typing race where short queries
  // (broad result set, slow response) used to overwrite long queries
  // (narrow result set, fast response) and revert the list to "almost full".
  const latestReqIdRef = useRef(0);
  const load = useCallback(() => {
    const myId = ++latestReqIdRef.current;
    setLoading(true);
    fetchCustomers({ country, per_page: 300, search: globalSearch })
      .then((d) => { if (myId === latestReqIdRef.current) setData(d); })
      .catch((e) => { if (myId === latestReqIdRef.current) console.error(e); })
      .finally(() => { if (myId === latestReqIdRef.current) setLoading(false); });
  }, [country, globalSearch]);
  useEffect(() => { load(); }, [load]);

  const [collapsedGroups, setCollapsedGroups] = useState({});
  // Column sort — overrides the default (day-of-week → Company Name) ordering within each week group.
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const handleSort = (col) => {
    if (sortKey !== col) { setSortKey(col); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortKey(""); setSortDir("asc"); // third click clears → default
  };

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    return data.items.filter((item) => {
      for (const col of COLUMNS) {
        const f = filters[col.key];
        if (!f || !f.value) continue;
        const val = String(item[col.key] || "").toLowerCase();
        if (!val.includes(f.value.toLowerCase())) return false;
      }
      return true;
    });
  }, [data?.items, filters]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const item of filteredItems) {
      const yw = item.Contact_Year_Week || 0;
      if (!map.has(yw)) map.set(yw, []);
      map.get(yw).push(item);
    }
    // Custom sort overrides the default day-of-week → company order within each week group.
    const customSort = sortKey ? (a, b) => {
      const col = COLUMNS.find((c) => c.key === sortKey);
      const accessor = sortKey === "Answered By" ? ((r) => r["Answered By Name"] || r["Answered By"] || "")
        : (col?.format === "money" ? ((r) => Number(r[sortKey]) || 0)
        : ((r) => r[sortKey]));
      const va = accessor(a), vb = accessor(b);
      const na = va == null || va === ""; const nb = vb == null || vb === "";
      if (na && nb) return 0;
      if (na) return 1;
      if (nb) return -1;
      if (typeof va === "number" && typeof vb === "number") return (sortDir === "desc" ? -1 : 1) * (va - vb);
      return (sortDir === "desc" ? -1 : 1) * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
    } : null;
    for (const items of map.values()) {
      if (customSort) {
        items.sort(customSort);
      } else {
        items.sort((a, b) => {
          const ra = getDaySortRank(a["Contact Date"]);
          const rb = getDaySortRank(b["Contact Date"]);
          if (ra !== rb) return ra - rb;
          return String(a["Company Name"] || "").localeCompare(String(b["Company Name"] || ""));
        });
      }
    }
    return Array.from(map.entries()).map(([yw, items]) => ({ key: yw, label: formatYearWeek(yw), items }));
  }, [filteredItems, sortKey, sortDir]);

  const toggleGroup = (key) => setCollapsedGroups((p) => ({ ...p, [key]: !p[key] }));
  const updateFilter = (key, value) => setFilters((p) => ({ ...p, [key]: { value } }));
  const clearFilters = () => setFilters({});
  const activeFilterCount = Object.values(filters).filter((f) => f.value && f.value !== "").length;

  const userEmail = (user?.email || "").toLowerCase();
  const isSuperEditor = SUPER_EDITOR_EMAILS.includes(userEmail);
  const hasEditColumn = user?.is_admin || isSuperEditor;
  const isCA = country === "CA";
  const colCount = COLUMNS.length + (hasEditColumn ? 1 : 0);
  const tableMinWidth = COLUMNS.reduce((s, c) => s + c.defW, 0) + (hasEditColumn ? 40 : 0);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-14 flex items-center gap-3">
          {/* iter150AZ.41 — Back-arrow returns the user to where they
              came from (history -1). The HeaderBrand logo still goes
              straight home, so users always have one always-home
              affordance and one contextual back. */}
          <button onClick={() => { try { navigate(-1); } catch { navigate("/"); } }} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" data-testid="customer-page-back" title={t("nav.back", "Back")}><ArrowLeft size={18} weight="bold" /></button>
          <HeaderBrand user={user} compact />
          <div className="flex-1 max-w-md relative hidden md:block">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="customer-search-input" placeholder={t("customer.search_placeholder", "Search name, address, email, phone, serial, invoice…")}
              className="pl-9 h-9 rounded-sm border-slate-300 text-sm" value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} />
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {user?.is_admin && <button onClick={() => navigate("/admin/settings")} data-testid="customer-page-admin-settings-btn" className="w-7 h-7 flex items-center justify-center rounded-sm border border-slate-200 hover:bg-slate-100 text-slate-500"><ShieldCheck size={14} weight="bold" /></button>}
            <span className="text-xs text-slate-500 hidden md:inline truncate max-w-[100px]">{user?.email || "Guest"}</span>
            <button onClick={onLogout} className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400"><SignOut size={14} /></button>
          </div>
        </div>
        {/* Mobile search */}
        <div className="md:hidden px-4 pb-2">
          <div className="relative">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder={t("customer.search_placeholder_short", "Search...")} className="pl-8 h-8 rounded-sm border-slate-300 text-sm"
              value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} />
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-3 md:p-8">
        {/* Tabs */}
        <div className="flex items-center gap-0.5 mb-4 md:mb-6 border-b border-slate-200 overflow-x-auto">
          {Object.entries(COUNTRY_CONFIG).map(([key, c]) => (
            <NavLink key={key} to={`/customers-${key.toLowerCase()}`} data-testid={`tab-customers-${key.toLowerCase()}`}
              className={`px-3 md:px-4 py-2 text-xs md:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${country === key ? c.tabActive : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {c.label}
            </NavLink>
          ))}
        </div>

        {/* Recently viewed strip — last 5 customers opened by this user.
            Tapped chip reopens the detail panel; small × removes the entry. */}
        {recentCustomers.length > 0 && (
          <div className="mb-3" data-testid="recent-customers-strip">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mr-1">
                {t("customer.recently_viewed", "Recently viewed")}
              </span>
              {recentCustomers.map((rc) => {
                const tIcon = customerTypeIcon(rc.Type);
                return (
                  <div key={rc.CustomerID} data-testid={`recent-chip-${rc.CustomerID}`}
                    className={`group inline-flex items-center gap-1.5 pl-2 pr-1 h-7 rounded-full border ${isCA ? "border-red-200 bg-red-50/60 hover:bg-red-100" : "border-blue-200 bg-blue-50/60 hover:bg-blue-100"} transition-colors cursor-pointer`}
                    onClick={() => openCustomer({ CustomerID: rc.CustomerID, "Company Name": rc["Company Name"] })}
                    title={`Reopen ${rc["Company Name"] || "—"}`}>
                    {tIcon ? <tIcon.Icon size={12} className={`${tIcon.color} shrink-0`} />
                      : <User size={12} weight="fill" className={`${cfg.accent} shrink-0`} />}
                    <span className="text-[11px] font-semibold text-slate-700 max-w-[180px] truncate">
                      {rc["Company Name"] || "—"}
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); removeRecentCustomer(rc.CustomerID); }}
                      data-testid={`recent-remove-${rc.CustomerID}`}
                      className="w-4 h-4 rounded-full text-slate-400 hover:text-rose-600 hover:bg-white/60 flex items-center justify-center"
                      title={t("customer.remove_recent", "Remove from recent")}>
                      <X size={10} weight="bold" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Count + Flag + Filters */}
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span data-testid="customer-total-count" className={`text-xs font-bold tracking-[0.15em] whitespace-nowrap ${cfg.accent}`}>
              {filteredItems.length}/{data?.total || 0} {cfg.label}
            </span>
            {cfg.flag && <div className="hidden md:block"><WavingFlag src={cfg.flag} width={90} height={56} speed={0.8} /></div>}
            {cfg.flag && <div className="md:hidden"><WavingFlag src={cfg.flag} width={50} height={32} speed={0.8} /></div>}
            <button data-testid="toggle-customer-filters" onClick={() => setShowFilters(!showFilters)}
              className={`text-xs font-semibold px-2 py-1 rounded-sm flex items-center gap-1 border ${
                showFilters || activeFilterCount > 0 ? cfg.accent + " " + cfg.accentBg + " " + cfg.accentBorder : "text-slate-500 bg-white border-slate-200"
              }`}>
              <Funnel size={12} weight={activeFilterCount > 0 ? "fill" : "bold"} />
              <span className="hidden md:inline">Filters{activeFilterCount > 0 && ` (${activeFilterCount})`}</span>
              {activeFilterCount > 0 && <span className="md:hidden">{activeFilterCount}</span>}
            </button>
            {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-slate-500 underline">{t("customer.clear_filters", "Clear")}</button>}
          </div>
          <button
            type="button"
            data-testid="customer-add-btn"
            onClick={() => setShowAddCustomer(true)}
            className={`text-xs font-bold uppercase tracking-wider h-8 px-3 rounded-sm text-white inline-flex items-center gap-1 ${isCA ? "bg-red-700 hover:bg-red-800" : "bg-blue-700 hover:bg-blue-800"}`}
          >
            {t("customer.add_button", "+ Add customer")}
          </button>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-slate-400" /></div>
        ) : (
          <>
            {/* Indeterminate progress bar — visible whenever we're refetching
               (country tab switch, new search, etc.). Without this the user
               sees stale data with no indication a request is in flight. */}
            {loading && (
              <div className="relative h-0.5 bg-slate-100 overflow-hidden mb-2 rounded-sm" data-testid="customer-list-loading-bar">
                <div className={`absolute inset-y-0 w-1/3 ${cfg.detailAccent} animate-loadingbar`} />
              </div>
            )}
            <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
            {/* ═══ MOBILE VIEW ═══ */}
            <div className="md:hidden space-y-1" style={{ maxHeight: "calc(100vh - 260px)", overflowY: "auto" }}>
              {grouped.length === 0 ? (
                <p className="text-center py-12 text-slate-400 text-sm">{t("customer.no_customers", "No customers found")}</p>
              ) : grouped.map(({ key, label, items }) => (
                <div key={key}>
                  {/* Group header — full width, not scrollable */}
                  <button onClick={() => toggleGroup(key)}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 ${cfg.accentBg} ${cfg.accentBorder} border-b`}>
                    {collapsedGroups[key] ? <CaretDown size={13} weight="bold" className={cfg.accent} /> : <CaretUp size={13} weight="bold" className={cfg.accent} />}
                    <span className={`text-xs font-bold font-['Chivo'] ${cfg.groupText}`}>{label}</span>
                    <span className={`text-[10px] font-semibold px-1 py-0.5 rounded-sm ${cfg.badge}`}>{items.length}</span>
                  </button>
                  {/* Customer rows — tap to open detail */}
                  {!collapsedGroups[key] && items.map((cust, idx) => {
                    const isOdd = idx % 2 === 1;
                    const typeIcon = customerTypeIcon(cust.Type);
                    return (
                      <button key={cust.id} data-testid={`mobile-customer-${cust.id}`}
                        onClick={() => openCustomer(cust)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 border-b border-slate-100 text-left transition-colors ${isOdd ? (isCA ? "bg-red-50/40" : "bg-slate-50") : "bg-white"} active:bg-slate-100`}>
                        <span className="flex items-center gap-1.5 truncate flex-1">
                          {typeIcon && <typeIcon.Icon size={14} className={`${typeIcon.color} shrink-0`} />}
                          {cust.Compressor_Owner === "Y" && <RotorIcon size={14} color={COUNTRY_COLOR[country]} className="shrink-0" />}
                          <span className="text-sm font-semibold text-slate-900 truncate">{cust["Company Name"] || "-"}</span>
                          {Number(cust.avg_quote) > 0 && (
                            <span className="text-[10px] font-mono text-slate-500 ml-auto shrink-0">${Number(cust.avg_quote).toLocaleString(undefined,{maximumFractionDigits:0})}</span>
                          )}
                        </span>
                        {/* iter150AU — per-row hierarchy launcher (mobile). */}
                        {cust.CustomerID && (
                          <span
                            role="button"
                            tabIndex={0}
                            data-testid={`mobile-customer-hierarchy-${cust.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/company-hierarchy?center=${encodeURIComponent(cust.CustomerID)}`);
                            }}
                            title={t("customer.hierarchy_row_title", "View company hierarchy of this customer")}
                            className="inline-flex items-center justify-center w-6 h-6 rounded-sm text-slate-400 hover:bg-indigo-50 hover:text-indigo-700 mr-1 shrink-0">
                            <TreeStructure size={14} weight="bold" />
                          </span>
                        )}
                        <CaretDown size={12} weight="bold" className="text-slate-300 -rotate-90 ml-2 shrink-0" />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* ═══ DESKTOP VIEW ═══ */}
            <div className="hidden md:block border border-slate-200 rounded-sm overflow-y-auto" style={{ maxHeight: "calc(100vh - 280px)" }}>
              {/* Group sections with full-width headers + scrollable table rows */}
              {grouped.length === 0 ? (
                <p className="text-center py-12 text-slate-400">{t("customer.no_customers", "No customers found")}</p>
              ) : grouped.map(({ key, label, items }) => (
                <div key={key}>
                  {/* Group header — full width, NOT inside scrollable area */}
                  <button onClick={() => toggleGroup(key)}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 ${cfg.accentBg} ${cfg.accentBorder} border-b cursor-pointer hover:opacity-80`}
                    data-testid={`customer-group-${key}`}>
                    {collapsedGroups[key] ? <CaretDown size={14} weight="bold" className={cfg.accent} /> : <CaretUp size={14} weight="bold" className={cfg.accent} />}
                    <span className={`text-sm font-bold font-['Chivo'] tracking-tight ${cfg.groupText}`}>{label}</span>
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-sm ${cfg.badge}`}>{items.length}</span>
                  </button>
                  {/* Scrollable table for this group */}
                  {!collapsedGroups[key] && (
                    <div className="overflow-x-scroll" style={{ WebkitOverflowScrolling: "touch" }}>
                      <table className="data-table border-collapse" style={{ tableLayout: "fixed", minWidth: `${tableMinWidth}px` }}>
                        <colgroup>
                          {hasEditColumn && <col style={{ width: "40px" }} />}
                          {COLUMNS.map((f) => <col key={f.key} style={{ width: `${f.defW}px` }} />)}
                        </colgroup>
                        {/* Show header only for the first visible group */}
                        {key === grouped.find(g => !collapsedGroups[g.key])?.key && (
                          <thead className="sticky top-0 z-20">
                            <tr className="bg-slate-50 border-b border-slate-200">
                              {hasEditColumn && <th className="px-1 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500 text-center bg-slate-50 sticky left-0 z-30 border-r-2 border-r-slate-300" style={{width:40}}>{t("customer.col_edit", "Edit")}</th>}
                              {COLUMNS.map((f) => {
                                const isSorted = sortKey === f.key;
                                // req #12 (iter110): some columns are
                                // explicitly NOT sortable (e.g. derived
                                // financial roll-ups). Render their label
                                // as a static span instead of a button.
                                const sortable = f.sortable !== false;
                                return (
                                <th key={f.key} className={`px-2 py-2 text-xs font-bold uppercase tracking-[0.1em] whitespace-nowrap select-none border-r border-slate-100 text-slate-500 bg-slate-50 ${f.align === "right" ? "text-right" : "text-left"} ${f.sticky && !hasEditColumn ? "sticky left-0 z-30 border-r-2 border-r-slate-300 bg-slate-50" : ""} ${f.sticky && hasEditColumn ? "sticky left-[40px] z-30 border-r-2 border-r-slate-300 bg-slate-50" : ""}`}>
                                  {sortable ? (
                                    <button type="button" onClick={() => handleSort(f.key)}
                                      data-testid={`customer-sort-${f.key}`}
                                      data-sort-active={isSorted ? sortDir : "off"}
                                      className={`inline-flex items-center gap-1 w-full ${f.align === "right" ? "justify-end" : "justify-start"} ${isSorted ? "text-slate-900" : "hover:text-slate-700"}`}
                                      title={isSorted ? `Sorted ${sortDir}` : "Click to sort"}>
                                      <span>{f.label}</span>
                                      <span className={isSorted ? "opacity-100" : "opacity-40"}>
                                        {isSorted ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                                      </span>
                                    </button>
                                  ) : (
                                    <span data-testid={`customer-col-${f.key}`}
                                      className={`inline-flex items-center gap-1 w-full ${f.align === "right" ? "justify-end" : "justify-start"} text-slate-500`}>
                                      {f.label}
                                    </span>
                                  )}
                                </th>
                              );})}
                            </tr>
                            {showFilters && (
                              <tr className="bg-slate-100/80 border-b border-slate-200">
                                {hasEditColumn && <td className="px-1 py-1 bg-slate-100 sticky left-0 z-30 border-r-2 border-r-slate-300" />}
                                {COLUMNS.map((f) => (
                                  <td key={f.key} className={`px-1 py-1 ${f.sticky && !hasEditColumn ? "sticky left-0 z-30 border-r-2 border-r-slate-300 bg-slate-100" : ""} ${f.sticky && hasEditColumn ? "sticky left-[40px] z-30 border-r-2 border-r-slate-300 bg-slate-100" : ""}`}>
                                    {f.filterType === "text" ? (
                                      <input data-testid={`filter-customer-${f.key}`} type="text" placeholder={t("customer.col_filter_placeholder", "Filter...")}
                                        value={filters[f.key]?.value || ""} onChange={(e) => updateFilter(f.key, e.target.value)}
                                        className="w-full h-6 text-[10px] px-1 border border-slate-300 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                    ) : null}
                                  </td>
                                ))}
                              </tr>
                            )}
                          </thead>
                        )}
                        <tbody>
                          {items.map((cust, idx) => {
                            const isOdd = idx % 2 === 1;
                            const oddBg = isCA ? "bg-red-50/50" : "bg-blue-50/40";
                            const oddSticky = isCA ? "bg-red-50" : "bg-blue-50";
                            const dayName = getDayOfWeek(cust["Contact Date"]);
                            const answeredBy = (cust["Answered By"] || "").toLowerCase();
                            const canEdit = isSuperEditor || (userEmail && answeredBy === userEmail);
                            return (
                              <tr key={cust.id} data-testid={`customer-row-${cust.id}`}
                                onClick={() => openCustomer(cust)}
                                className={`border-b border-slate-100 transition-colors cursor-pointer ${isOdd ? oddBg : "bg-white"} ${cfg.hoverRow}`}>
                                {hasEditColumn && (
                                  <td onClick={(e) => e.stopPropagation()} className={`px-1 py-1.5 text-center sticky left-0 z-10 border-r-2 border-r-slate-200 ${isOdd ? oddSticky : "bg-white"}`}>
                                    <div className="flex items-center justify-center gap-1">
                                      {canEdit ? (
                                        <button data-testid={`customer-edit-${cust.id}`}
                                          className={`inline-flex items-center justify-center w-6 h-6 rounded-sm border transition-colors text-slate-500 ${isCA ? "border-red-200 hover:bg-red-50 hover:text-[#D80621]" : "border-slate-200 hover:bg-slate-100 hover:text-blue-700"}`}>
                                          <PencilSimple size={13} />
                                        </button>
                                      ) : <span className="text-slate-300 text-[10px]">—</span>}
                                      <RecordButton customerId={cust.CustomerID || cust.id}
                                        customerName={cust["Company Name"] || ""} />
                                    </div>
                                  </td>
                                )}
                                {COLUMNS.map((f) => {
                                  let cellVal = formatCell(cust[f.key], f.format, f);
                                  if (f.key === "Answered By" && cust["Answered By Name"]) {
                                    cellVal = cust["Answered By Name"];
                                  }
                                  const isCompanyName = f.key === "Company Name";
                                  const showRotor = isCompanyName && cust.Compressor_Owner === "Y";
                                  const typeIcon = isCompanyName ? customerTypeIcon(cust.Type) : null;
                                  const dayBadgeCls = dayName ? (DAY_BADGE_STYLE[dayName] || "bg-slate-100 text-slate-600 border-slate-200") : "";
                                  return (
                                    <td key={f.key} className={`px-2 py-1.5 text-sm overflow-hidden text-ellipsis whitespace-nowrap border-r border-slate-50 ${
                                      f.align === "right" ? "text-right font-mono" : "text-left"
                                    } ${isCompanyName ? "font-semibold text-slate-900" : "text-slate-700"} ${
                                      f.format === "money" && f.key === "Credit" ? "text-green-700" : ""} ${f.format === "money" && f.key === "Debit" ? "text-red-600" : ""} ${
                                      f.format === "money" && f.key === "avg_quote" ? "text-slate-700 font-semibold" : ""} ${
                                      f.sticky && !hasEditColumn ? `sticky left-0 z-10 border-r-2 border-r-slate-200 ${isOdd ? oddSticky : "bg-white"}` : ""} ${
                                      f.sticky && hasEditColumn ? `sticky left-[40px] z-10 border-r-2 border-r-slate-200 ${isOdd ? oddSticky : "bg-white"}` : ""}`}
                                      title={cust[f.key] != null ? String(cust[f.key]) : ""}>
                                      {isCompanyName ? (
                                        <span className="inline-flex items-center gap-1.5">
                                          {dayName && (
                                            <span data-testid={`day-badge-${cust.id}`}
                                              className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${dayBadgeCls} shrink-0`}>
                                              {dayName}
                                            </span>
                                          )}
                                          {typeIcon && <typeIcon.Icon size={14} className={`${typeIcon.color} shrink-0`} title={typeIcon.title} />}
                                          {showRotor && <RotorIcon size={13} color={COUNTRY_COLOR[country]} className="shrink-0" />}
                                          <span className="truncate">{cellVal}</span>
                                          {/* iter150AU — per-row hierarchy launcher.
                                              Click opens the tree centered on
                                              THIS company. Available to every
                                              user; access is gated by the page
                                              route itself. */}
                                          {cust.CustomerID && (
                                            <button
                                              type="button"
                                              data-testid={`customer-hierarchy-${cust.id}`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/company-hierarchy?center=${encodeURIComponent(cust.CustomerID)}`);
                                              }}
                                              title={t("customer.hierarchy_row_title", "View company hierarchy of this customer")}
                                              className="inline-flex items-center justify-center w-5 h-5 rounded-sm text-slate-400 hover:bg-indigo-50 hover:text-indigo-700 transition-colors shrink-0 ml-0.5">
                                              <TreeStructure size={13} weight="bold" />
                                            </button>
                                          )}
                                        </span>
                                      ) : f.key === "avg_quote" && cust.quote_count > 0 ? (
                                        <span title={`${cust.quote_count} quote(s)`}>{cellVal}</span>
                                      ) : f.key === "avg_quote" ? (
                                        <span className="text-slate-300">—</span>
                                      ) : cellVal}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
            </div>
          </>
        )}
      </main>

      {/* Customer Detail Panel (responsive modal) */}
      {selectedCustomer && (
        <CustomerDetail customer={selectedCustomer} onClose={() => setSelectedCustomer(null)} cfg={cfg} isCA={isCA} user={user} />
      )}

      {showAdmin && <AdminPanel user={user} onClose={() => setShowAdmin(false)} />}
      {showAddCustomer && (
        <AddCustomerModal
          user={user}
          country={country}
          isCA={isCA}
          selfService={false}
          onClose={() => setShowAddCustomer(false)}
          onCreated={(res) => {
            setShowAddCustomer(false);
            // Reload the current page so the new row appears, then auto-open
            // the new customer's detail panel.
            load();
            setTimeout(() => {
              openCustomer({ CustomerID: res.customer_id, "Company Name": "" });
            }, 600);
          }}
        />
      )}
    </div>
  );
}

function CustomerDetail({ customer, onClose, cfg, isCA, user }) {
  const c = customer;
  const t = useT();
  const navigate = useNavigate();
  const ccy = (c.Currency || "USD").toUpperCase();
  const contactDate = c["Contact Date"] ? new Date(c["Contact Date"]).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-";
  // iter150AZ.56 — when the customer prop arrived without a Company Name
  // (deep-link / focus param / newly-created customer stub) we fetch the
  // canonical row so the header NEVER shows the raw CustomerID hex.
  const [companyName, setCompanyName] = useState(c["Company Name"] || "");
  useEffect(() => {
    setCompanyName(c["Company Name"] || "");
  }, [c.CustomerID, c["Company Name"]]);
  useEffect(() => {
    if (!c.CustomerID) return;
    if ((c["Company Name"] || "").trim()) return;
    let cancelled = false;
    fetchCustomerSummary(c.CustomerID)
      .then((row) => {
        if (cancelled) return;
        const name = (row && (row["Company Name"] || row.company_name)) || "";
        if (name) setCompanyName(name);
      })
      .catch(() => { /* network noise — keep showing whatever we have */ });
    return () => { cancelled = true; };
  }, [c.CustomerID, c["Company Name"]]);
  const [persons, setPersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [compressors, setCompressors] = useState([]);
  const [parts, setParts] = useState([]);
  const [journal, setJournal] = useState([]);
  const [notes, setNotes] = useState([]);
  const [phoneLogs, setPhoneLogs] = useState([]);

  // iter150AZ.34 — react-query hooks share cache with CompressorDetailModal,
  // CompressorPmViewerModal, ServiceHistoryPopover. Switching customers or
  // re-opening the same detail panel within 30s costs zero extra GETs.
  const queryClient = useQueryClient();
  const cid = c.CustomerID || "";
  const personsQ     = useCustomerPersons(cid);
  const quotesQ      = useCustomerQuotes(cid, 50);
  const compressorsQ = useCustomerCompressors(cid);
  const partsQ       = useCustomerPartsPurchased(cid, { limit: 200 });
  const journalQ     = useCustomerJournal(cid);
  const notesQ       = useCustomerNotes(cid);
  const phoneLogsQ   = useCustomerPhoneLogs(cid);

  useEffect(() => { setPersons(personsQ.data?.persons || []); }, [personsQ.data]);
  useEffect(() => { setLoadingPersons(personsQ.isLoading); }, [personsQ.isLoading]);
  useEffect(() => { setQuotes(quotesQ.data?.quotes || []); }, [quotesQ.data]);
  useEffect(() => { setLoadingQuotes(quotesQ.isLoading); }, [quotesQ.isLoading]);
  useEffect(() => { setCompressors(compressorsQ.data?.items || []); }, [compressorsQ.data]);
  useEffect(() => { setParts(partsQ.data?.items || []); }, [partsQ.data]);
  useEffect(() => { setJournal(journalQ.data?.items || []); }, [journalQ.data]);
  useEffect(() => { setNotes(notesQ.data?.items || []); }, [notesQ.data]);
  useEffect(() => { setPhoneLogs(phoneLogsQ.data?.items || []); }, [phoneLogsQ.data]);
  // iter150AZ.41 — local copy of the two addresses so inline-edit can
  // patch them without forcing a full customer reload. Initialised
  // from the customer prop; mutated on successful PATCH.
  const [addresses, setAddresses] = useState({
    billTo: c["Bill to Address"] || "",
    shipTo: c["Address"] || c.Address || "",
  });
  useEffect(() => {
    setAddresses({
      billTo: c["Bill to Address"] || "",
      shipTo: c["Address"] || c.Address || "",
    });
  }, [c.CustomerID, c["Bill to Address"], c["Address"]]);
  const [openQuote, setOpenQuote] = useState(null);
  const [openCompressor, setOpenCompressor] = useState(null);
  const [showSendFiles, setShowSendFiles] = useState(false);
  // iter150AZ.41 — guided coachmark tour state. ``tourTick`` is
  // incremented to force a re-launch when the user clicks the Help
  // button. ``CustomerDetailTour`` handles the first-visit auto-run
  // itself via localStorage.
  const [tourTick, setTourTick] = useState(0);
  // iter150AZ.22 — Quote-type picker (Sales / Warranty / Return)
  const [showQuoteTypeMenu, setShowQuoteTypeMenu] = useState(false);
  const [warrantyClaimPickerOpen, setWarrantyClaimPickerOpen] = useState(false);
  const [recordingsTick, setRecordingsTick] = useState(0);
  const [creatingQuote, setCreatingQuote] = useState(false);

  // iter150AZ — Create Quote permission gate. Admins always, plus anyone
  // whose email is on the company sales domains.
  const userEmailLC = (user?.email || "").toLowerCase();
  const canCreateQuote = !!user && (
    user.is_admin === true ||
    userEmailLC.endsWith("@usaircompressor.com") ||
    userEmailLC.endsWith("@aircompressorcanada.com")
  );
  // iter150AZ.2 — Only super-admins get the "Set tier" deep-link on
  // tier-less quotes. Mirrors the SUPER_EDITOR_EMAILS allowlist at the
  // top of this file.
  const isSuperAdminUser = !!user && user.is_admin === true &&
    SUPER_EDITOR_EMAILS.includes(userEmailLC);

  const handleCreateQuote = async (payload = {}) => {
    if (!c.CustomerID || creatingQuote) return;
    setCreatingQuote(true);
    try {
      const r = await api.post(
        `/customers/${encodeURIComponent(c.CustomerID)}/quotes`,
        payload,
      );
      const qid = r?.data?.quote_id;
      const tier = r?.data?.tier;
      const prefill = r?.data?.prefill;
      if (qid) {
        // iter150AZ.34 — the new quote needs to show up in this customer's
        // quote list immediately on back-navigation; nuke the cache.
        queryClient.invalidateQueries({ queryKey: ["customer-quotes", c.CustomerID, 50] });
        // Compose a tier-aware toast so the rep immediately sees which
        // Customer_Aux discounts will auto-apply to the new lines.
        const bits = [];
        if (tier?.payment_label) bits.push(tier.payment_label);
        if (tier?.discount_pct_final_d_series != null) bits.push(`D-series ${tier.discount_pct_final_d_series}% off`);
        if (tier?.discount_pct_final_b_series != null) bits.push(`B-series ${tier.discount_pct_final_b_series}% off`);
        if (tier?.discount_pct_spare_parts != null) bits.push(`Parts ${tier.discount_pct_spare_parts}% off`);
        const detail = bits.length ? bits.join(" · ") : t("customer.quote_no_tier", "No discount tier on file");
        // iter150AZ.23 — surface the warranty auto-prefill so reps know
        // lines were inserted from the claim's failed-parts list.
        const prefilledLines = Number(prefill?.prefilled_lines || 0);
        const prefillMsg = prefilledLines > 0
          ? t("customer.quote_prefilled", "{{n}} line{{s}} auto-added from warranty claim ({{src}}).", {
              n: prefilledLines,
              s: prefilledLines === 1 ? "" : "s",
              src: prefill?.source === "parts_snapshot" ? "filed parts list" : "PM service kit",
            })
          : "";
        // Super-admins get a one-click deep-link to Customer_Aux when
        // the customer has no tier set — opens a new tab so the quote
        // they were just redirected to stays open.
        const showSetTier = isSuperAdminUser && tier && tier.has_tier_on_file === false;
        const toastOpts = { description: detail, duration: showSetTier ? 8000 : 5000 };
        if (showSetTier) {
          toastOpts.action = {
            label: t("customer.set_tier_action", "Set tier"),
            onClick: () => {
              window.open(
                `/admin/customer-aux?q=${encodeURIComponent(c.CustomerID)}`,
                "_blank",
                "noopener,noreferrer",
              );
            },
          };
        }
        toast.success(
          prefillMsg
            ? t("customer.quote_created_with_prefill_toast", "Warranty quote created · {{msg}}", { msg: prefillMsg })
            : t("customer.quote_created_toast", "Quote created."),
          toastOpts,
        );
        navigate(`/quote/${encodeURIComponent(qid)}`);
      } else {
        toast.error(t("customer.quote_create_failed", "Could not create quote."));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("customer.quote_create_failed", "Could not create quote."));
    } finally {
      setCreatingQuote(false);
    }
  };

  // iter150AZ.34 — react-query owns the data-fetch lifecycle now.
  // This effect is just the auto-record prompt (preserved verbatim).
  useEffect(() => {
    if (!c.CustomerID) return;
    try {
      const raw = sessionStorage.getItem("__field_tools_offer_record");
      if (raw) {
        const stash = JSON.parse(raw);
        if (stash?.customer_id === c.CustomerID && (Date.now() - (stash.at || 0)) < 5 * 60 * 1000) {
          sessionStorage.removeItem("__field_tools_offer_record");
          if (window.confirm("Start recording this conversation now?")) {
            // Click the existing record pill in the header.
            setTimeout(() => {
              const btn = document.querySelector(`[data-testid="customer-detail-record-${c.CustomerID}"]`);
              btn && btn.click();
            }, 200);
          }
        }
      }
    } catch { /* noop */ }
  }, [c.CustomerID]);

  const refreshQuotes = useCallback(() => {
    if (!c.CustomerID) return;
    queryClient.invalidateQueries({ queryKey: ["customer-quotes", c.CustomerID, 50] });
  }, [c.CustomerID, queryClient]);

  const refreshPersons = useCallback(() => {
    if (!c.CustomerID) return;
    queryClient.invalidateQueries({ queryKey: ["customer-persons", c.CustomerID] });
  }, [c.CustomerID, queryClient]);

  // iter150AZ.37 — derive a "YYYY-MM" stamp under the company name.
  const contactYm = (() => {
    const raw = c["Contact Date"] || contactDate;
    if (!raw) return "";
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString("en-US", { year: "numeric", month: "short" });
    } catch { return ""; }
  })();

  return (
    <div className="fixed inset-0 z-50" data-testid="customer-detail-panel">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      {/* iter150AZ.37 — full-screen on desktop too, preserving the top
          app menu (top-16). Mobile keeps the same offset. */}
      <div className="absolute inset-x-0 bottom-0 top-16 bg-white shadow-xl overflow-y-auto md:border-t md:border-slate-200">
        {/* Header bar — iter150AZ.56:
            Two-row layout so the company name always stays prominent and is
            never squeezed to 0-width by the adjacent action buttons. Row 1
            is purely the customer identity (name + AI badge + close).
            Row 2 holds the help pill and the action buttons. */}
        <div className={`sticky top-0 z-10 px-4 py-2 border-b ${isCA ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`}
             data-tour="customer-header">
          {/* Row 1: company identity */}
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-1 min-w-0">
              <h2
                className="text-lg md:text-xl font-bold font-['Chivo'] text-slate-900 truncate leading-tight"
                data-testid={`customer-detail-company-name-${c.CustomerID}`}
                title={companyName || ""}
              >
                {companyName || (
                  <span className="italic text-slate-400 text-base">
                    {t("customer.loading_company_name", "Loading company name…")}
                  </span>
                )}
              </h2>
              {contactYm && (
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mt-0.5" data-testid="customer-detail-contact-ym">
                  {contactYm}
                </p>
              )}
            </div>
            <AIProvenanceBadge entityType="customer" entityId={c.CustomerID} userEmail={user?.email} />
            <button
              onClick={onClose}
              data-testid={`customer-detail-close-${c.CustomerID}`}
              title="Close"
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-500 hover:text-slate-800 hover:border-slate-400"
            >
              <X size={16} />
            </button>
          </div>

          {/* Row 2: actions */}
          <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
            <div className="flex items-center gap-2">
              <TourHelpButton scope="customer_detail" onClick={() => setTourTick((n) => n + 1)} />
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                data-testid={`customer-send-files-btn-${c.CustomerID}`}
                onClick={() => setShowSendFiles(true)}
                title={t("customer.send_files_tooltip", "Send Public Files to this customer")}
                className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-blue-700 text-white hover:bg-blue-800"
              >
                <PaperPlaneTilt size={11} weight="bold" /> Send Files
              </button>
              {canCreateQuote && (
                <div className="relative" data-testid={`customer-create-quote-menu-${c.CustomerID}`}>
                  <button
                    type="button"
                    data-testid={`customer-create-quote-btn-${c.CustomerID}`}
                    onClick={() => setShowQuoteTypeMenu(v => !v)}
                    disabled={creatingQuote}
                    title={t("customer.create_quote_tooltip", "Start a new Preliminary quote for this customer")}
                    className={`inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase tracking-wider rounded-sm text-white disabled:opacity-60 ${isCA ? "bg-[#D80621] hover:bg-red-800" : "bg-emerald-700 hover:bg-emerald-800"}`}
                  >
                    {creatingQuote ? (
                      <Spinner size={11} weight="bold" className="animate-spin" />
                    ) : (
                      <FileText size={11} weight="bold" />
                    )}
                    {creatingQuote
                      ? t("customer.creating_quote", "Creating…")
                      : t("customer.create_quote", "Create Quote")}
                    {!creatingQuote && <CaretDown size={9} weight="bold" />}
                  </button>
                  {showQuoteTypeMenu && !creatingQuote && (
                    <div
                      className="absolute right-0 top-full mt-1 z-30 w-44 bg-white border border-slate-200 rounded-sm shadow-lg overflow-hidden"
                      onMouseLeave={() => setShowQuoteTypeMenu(false)}
                    >
                      <button
                        data-testid={`customer-create-quote-sales-${c.CustomerID}`}
                        onClick={() => { setShowQuoteTypeMenu(false); handleCreateQuote({ q_type: "Sales" }); }}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-800 flex items-center gap-2"
                      >
                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                        {t("customer.quote_type_sales", "Sales")}
                      </button>
                      <button
                        data-testid={`customer-create-quote-warranty-${c.CustomerID}`}
                        onClick={() => { setShowQuoteTypeMenu(false); setWarrantyClaimPickerOpen(true); }}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-amber-50 hover:text-amber-800 flex items-center gap-2 border-t border-slate-100"
                      >
                        <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                        {t("customer.quote_type_warranty", "Warranty…")}
                      </button>
                      <button
                        data-testid={`customer-create-quote-return-${c.CustomerID}`}
                        onClick={() => { setShowQuoteTypeMenu(false); handleCreateQuote({ q_type: "Return" }); }}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-rose-50 hover:text-rose-800 flex items-center gap-2 border-t border-slate-100"
                      >
                        <span className="inline-block w-2 h-2 rounded-full bg-rose-500" />
                        {t("customer.quote_type_return", "Return")}
                      </button>
                    </div>
                  )}
                </div>
              )}
              <RecordButton variant="pill"
                customerId={c.CustomerID}
                customerName={companyName || ""}
                testId={`customer-detail-record-${c.CustomerID}`}
                onSubmitted={() => setRecordingsTick((n) => n + 1)} />
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4 max-w-5xl mx-auto">
          {/* iter150AZ.41 — guided coachmark tour. Auto-launches on
              first visit; re-launchable from the Help button. */}
          <CustomerDetailTour runFlag={tourTick} />

          {/* iter150AZ.37 — Terms block at the top (placeholder until
              batch B wires the data feed). */}
          <TermsSection customer={c} isCA={isCA} userEmail={user?.email} />

          {/* People at this customer */}
          <PeopleSection
            persons={persons}
            loading={loadingPersons}
            customerId={c.CustomerID}
            companyName={c["Company Name"]}
            isCA={isCA}
            user={user}
            onChanged={refreshPersons}
          />

          {/* iter150M — Past in-person conversation recordings (auto-hides for non-field users / zero rows) */}
          <CustomerRecordingsSection
            customerId={c.CustomerID}
            customerName={c["Company Name"]}
            refreshKey={recordingsTick}
          />

          {/* Quotes & Invoices */}
          <div data-tour="quotes">
            <QuotesSection quotes={quotes} loading={loadingQuotes} isCA={isCA} user={user} onQuoteMutated={refreshQuotes} />
          </div>

          {/* Compressors / Final Products owned */}
          <div data-tour="compressors">
            <CompressorsOwnedSection items={compressors} isCA={isCA} onOpenCompressor={(serial) => setOpenCompressor(serial)} />
          </div>

          {/* Parts Purchased */}
          <div data-tour="parts">
            <PartsPurchasedSection items={parts} ccy={ccy} isCA={isCA} onOpenInvoice={(hid) => setOpenQuote({ QuoteInvoiceHeadingID: hid })} />
          </div>

          {/* iter149e — Phone-call history for this customer */}
          <PhoneLogsSection items={phoneLogs} isCA={isCA} />

          {/* Customer-specific Notes & files (filtered by role on the backend) */}
          <div data-tour="notes">
            <NotesListSection
              items={notes}
              isCA={isCA}
              customerId={c.CustomerID}
              onAddNote={async (text) => {
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${encodeURIComponent(c.CustomerID)}/notes`,
                  { action: "Note", explanation: text },
                  { withCredentials: true, headers: user?.email ? { "X-User-Email": user.email } : {} }
                );
                fetchCustomerNotes(c.CustomerID).then(d => setNotes(d.items || [])).catch(() => {});
              }}
            />
          </div>

          <div data-tour="addresses">
            <AddressesSection
              customerId={c.CustomerID}
              billTo={addresses.billTo}
              shipTo={addresses.shipTo}
              canEdit={canCreateQuote}
              onSaved={(b, s) => setAddresses({ billTo: b ?? addresses.billTo, shipTo: s ?? addresses.shipTo })}
            />
          </div>

          {(c._Notes || c["notes special"]) && (
            <DetailSection title={t("customer.detail_notes", "Notes (Customer table)")}>
              {c._Notes && <p className="text-sm text-slate-700">{c._Notes}</p>}
              {c["notes special"] && <p className="text-sm text-slate-500 mt-1">{c["notes special"]}</p>}
            </DetailSection>
          )}

          {/* iter150AZ.37 — Journal entries pinned to the bottom of the
              panel (was higher up before this iteration). */}
          <div data-tour="journal">
            <JournalSection items={journal} ccy={ccy} isCA={isCA} />
          </div>
        </div>
        {openQuote && <QuoteDetailModal headingId={openQuote.QuoteInvoiceHeadingID} onClose={() => { setOpenQuote(null); refreshQuotes(); }} user={user} />}
        {openCompressor && (
          <CompressorDetailModal
            serialId={openCompressor}
            customerId={c.CustomerID}
            isCA={isCA}
            user={user}
            onOpenInvoice={(hid) => setOpenQuote({ QuoteInvoiceHeadingID: hid })}
            onClose={() => setOpenCompressor(null)}
          />
        )}
        <SendPublicFilesModal
          open={showSendFiles}
          onClose={() => setShowSendFiles(false)}
          customerId={c.CustomerID}
          recipients={persons
            .map((p) => (p["Main Email"] || "").trim().toLowerCase())
            .filter(Boolean)
            .slice(0, 5)}
          title={`Send documents to ${companyName || "customer"}`}
        />
        {/* iter150AZ.22 — claim picker for the Warranty quote flow. */}
        <WarrantyClaimPickerModal
          customerId={c.CustomerID}
          customerName={companyName || ""}
          isOpen={warrantyClaimPickerOpen}
          onClose={() => setWarrantyClaimPickerOpen(false)}
          onPick={(claimId) => {
            setWarrantyClaimPickerOpen(false);
            handleCreateQuote({
              q_type: "Warranty",
              warranty_claim_id: claimId,
            });
          }}
        />
      </div>
    </div>
  );
}

function DetailSection({ title, children, compact }) {
  return (
    <div>
      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">{title}</h4>
      <div className={compact ? "grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1" : "space-y-1.5"}>{children}</div>
    </div>
  );
}

// ─── New Wave-A sections ────────────────────────────────────────────
function fmtMoney(v, ccy = "USD") {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (isNaN(n)) return "—";
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 2 }).format(n); }
  catch { return `${n.toFixed(2)}`; }
}
function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }); }
  catch { return String(d); }
}

/**
 * iter150AZ.36 — Collapsible section wrapper for the Customer Detail
 * panel. All 7 sub-sections (Compressors, Parts, Journal, PhoneLogs,
 * People, Notes, Quotes) wrap themselves in this so reps can scan the
 * panel without scrolling past dense lists they don't care about.
 *
 * Default state is COLLAPSED — click the header to expand. Toggling
 * preserves component state below (react-query cache is intact), so
 * re-opening is instant.
 */
function CollapsibleSection({ title, count, icon: Icon, headerExtra, children, testid, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  // iter150AZ.53 — ``headerExtra`` can be either a React node OR a
  // function ``({ open }) => node`` so callers can choose to suppress
  // their controls when the section is collapsed (the Parts-Purchased
  // "Group: Kind | Type | Flat" bar uses this to disappear when the
  // section is closed).
  const extra = typeof headerExtra === "function"
    ? headerExtra({ open })
    : headerExtra;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid={`${testid}-toggle`}
          aria-expanded={open}
          className="flex-1 min-w-0 text-left flex items-center gap-1.5 group hover:opacity-80"
        >
          <CaretDown
            size={9}
            weight="bold"
            className={`shrink-0 text-slate-500 transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
          />
          <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1.5 group-hover:text-slate-600">
            {Icon ? <Icon size={11} weight="bold" /> : null}
            <span>{title}</span>
            {typeof count === "number" && count > 0 && (
              <span className="text-slate-500 normal-case tracking-normal">({count})</span>
            )}
          </h4>
        </button>
        {extra}
      </div>
      {open && children}
    </div>
  );
}

/**
 * iter150AZ.37 — useSortable + SortHeader: click a column header in
 * any list section to sort by that column. Toggles asc → desc → none.
 *
 * Usage::
 *
 *   const { rows, sort, toggle } = useSortable(items, "quote_date", "desc");
 *   ...
 *   <SortHeader sort={sort} field="invoice_number" onClick={toggle}>Invoice</SortHeader>
 *
 * The hook handles numeric, string, and date values. Pass a custom
 * ``mapValue`` if a row's column needs computation.
 */
function useSortable(rows, defaultField, defaultDir = "asc", mapValue) {
  const [sort, setSort] = useState({ field: defaultField, dir: defaultDir });
  const toggle = (field) => {
    setSort((cur) => {
      if (cur.field !== field) return { field, dir: "asc" };
      if (cur.dir === "asc") return { field, dir: "desc" };
      // 3rd click: revert to default (so reps can "unsort" without reload).
      return { field: defaultField, dir: defaultDir };
    });
  };
  const sortedRows = useMemo(() => {
    if (!sort.field || !Array.isArray(rows)) return rows || [];
    const out = [...rows];
    out.sort((a, b) => {
      const av = mapValue ? mapValue(a, sort.field) : a?.[sort.field];
      const bv = mapValue ? mapValue(b, sort.field) : b?.[sort.field];
      // Nullish always sinks to the bottom regardless of direction —
      // matches AppSheet / Excel default behaviour.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      // Try numeric first.
      const an = Number(av), bn = Number(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn) && av !== "" && bv !== "") {
        return sort.dir === "asc" ? an - bn : bn - an;
      }
      // Then ISO-date-ish strings via Date.parse.
      const ad = Date.parse(av), bd = Date.parse(bv);
      if (!Number.isNaN(ad) && !Number.isNaN(bd)) {
        return sort.dir === "asc" ? ad - bd : bd - ad;
      }
      // Fall back to string compare.
      const as = String(av).toLowerCase(), bs = String(bv).toLowerCase();
      if (as < bs) return sort.dir === "asc" ? -1 : 1;
      if (as > bs) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return out;
  }, [rows, sort, mapValue]);
  return { rows: sortedRows, sort, toggle };
}

function SortHeader({ field, sort, onClick, children, className = "", testid }) {
  const active = sort?.field === field;
  const arrow = !active ? "" : (sort.dir === "asc" ? " ▲" : " ▼");
  return (
    <button
      type="button"
      data-testid={testid || `sort-${field}`}
      onClick={() => onClick(field)}
      className={`text-left text-[9px] font-bold uppercase tracking-wider ${active ? "text-slate-800" : "text-slate-500"} hover:text-slate-900 ${className}`}
    >
      {children}{arrow}
    </button>
  );
}

/**
 * iter150AZ.37 / .38 — Customer "Terms" block. Replaces the old
 * standalone "Financials" card. Pulls the AppSheet-style fields
 * (Currency, Payment Terms, Credit Limit / Left / Balance, Discounts,
 * Sales Tax, Labor + Travel rates) from
 * ``GET /customers/{id}/terms`` and renders them as a clean labelled
 * table at the top of the People section.
 *
 * The "Total Debited / Actual Credited / Balance" rows are only
 * exposed for staff whose email contains ``@usacompressor.com`` —
 * matching the AppSheet formula's email gate.
 */
function TermsSection({ customer, isCA, userEmail }) {
  const customerId = customer?.CustomerID;
  const showStaffRows = !!(userEmail && /@usacompressor\.com\b/i.test(userEmail));
  const { data, isLoading } = useQuery({
    queryKey: ["customer-terms", customerId],
    queryFn: async () => {
      const r = await api.get(
        `/customers/${encodeURIComponent(customerId)}/terms`,
      );
      return r.data;
    },
    enabled: !!customerId,
  });
  if (!customerId) return null;
  const ccy = data?.currency || customer?.Currency || "USD";
  const num = (v) => (v == null || v === "" ? null : Number(v));
  // Rows are only rendered when they have a meaningful value (matching
  // the AppSheet `if(any(...)>0, ..., "")` pattern). ``null`` skips.
  const rows = [
    ["Company Currency",          ccy],
    [`Payment Terms`,             num(data?.payment_term) > 0 ? `${num(data?.payment_term)} Days` : null],
    [`Total Credit Limit (${ccy})`, num(data?.open_account_limit) > 0 ? fmtMoney(data?.open_account_limit, ccy) : null],
    [`Credit Limit Left (${ccy})`,  num(data?.open_account_limit) > 0 ? fmtMoney(data?.credit_left,        ccy) : null],
    [`Balance (${ccy})`,            num(data?.open_account_limit) > 0 ? fmtMoney(data?.balance,            ccy) : null],
    [`Discount for Final Product`,  num(data?.discount_d_series) > 0 ? `${num(data?.discount_d_series)} %` : null],
    [`Discount for Parts / Oil`,    num(data?.discount_spare_part) > 0 ? `${num(data?.discount_spare_part)} %` : null],
    [`Sales Tax`,                   data?.sales_tax == null || data?.sales_tax === "" ? "Not Calculated" : `${data?.sales_tax}`],
    [`Labor Cost / Hour`,           data?.labor != null ? `${data?.labor} (min ${data?.min_labor_time || 0} h)` : null],
    [`Travel Cost / Hour`,          data?.milage != null ? `${data?.milage} (min ${data?.min_travel_time || 0} h)` : null],
    ...(showStaffRows ? [
      [`Total Debited Amount (${ccy})`,    fmtMoney(data?.debit,                ccy)],
      [`Actual Credited Amount (${ccy})`,  fmtMoney(data?.credit_without_payterm, ccy)],
      [`Net Balance (${ccy})`,             fmtMoney(data?.net_balance,          ccy)],
    ] : []),
  ].filter(([, v]) => v !== null && v !== undefined && v !== "");
  return (
    <CollapsibleSection
      testid="customer-terms-section"
      title="Terms & Pricing"
      icon={Receipt}
      defaultOpen={false}
    >
      {isLoading ? (
        <div className="flex items-center gap-2 py-2 text-xs text-slate-400" data-testid="customer-terms-loading">
          <Spinner size={14} className="animate-spin" /> Loading terms…
        </div>
      ) : (
        <div className={`rounded-md border ${isCA ? "border-red-100" : "border-blue-100"} overflow-hidden`}>
          {rows.map(([label, value], i) => (
            <div
              key={label}
              data-testid={`customer-terms-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`min-w-[700px] flex items-center gap-2 px-2 py-1.5 text-[11px] ${i % 2 === 0 ? "bg-white" : (isCA ? "bg-red-50/40" : "bg-blue-50/40")} border-b last:border-b-0 ${isCA ? "border-red-100" : "border-blue-100"}`}
            >
              <div className="flex-1 min-w-0 text-slate-600 truncate">{label}</div>
              <div className="shrink-0 font-mono font-semibold text-slate-800 text-right">{value}</div>
            </div>
          ))}
          {rows.length === 0 && (
            <div className="p-2 text-center text-[11px] text-slate-400 italic">No terms data set for this customer.</div>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
}



function CompressorsOwnedSection({ items, isCA, onOpenCompressor }) {
  const t = useT();
  // iter150AX — when set, opens the PIN generator dialog for that
  // compressor row. ``pinFor`` holds {serial_number, product_name,
  // pin_type} so the dialog can show context.
  const [pinFor, setPinFor] = useState(null);
  // iter150AZ.32 — when set, opens the read-only PM schedule modal
  // for that compressor.
  const [pmFor, setPmFor] = useState(null);
  // iter150AZ.37 — sortable headers.
  const { rows: sortedItems, sort, toggle } = useSortable(
    items, "date_delivered", "desc",
  );
  if (!items || items.length === 0) return null;
  return (
    <CollapsibleSection
      testid="customer-compressors-section"
      title={t("customer.compressors_title", "Compressors / Final Products owned")}
      count={items.length}
      icon={Receipt}
    >
      <div className={`rounded-md border ${isCA ? "border-red-100" : "border-blue-100"} overflow-hidden`}>
        {/* Sortable header */}
        <div className={`min-w-[700px] sticky top-0 z-10 flex items-center gap-2 px-2 py-1 border-b ${isCA ? "bg-red-50 border-red-100" : "bg-blue-50 border-blue-100"}`}>
          <SortHeader field="product_type"   sort={sort} onClick={toggle} className="shrink-0 w-10">{t("ctable.type", "Type")}</SortHeader>
          {/* iter150AZ.53 — PM button column slot precedes the SN so
              the PM affordance reads as part of the serial-number
              identity for any owned compressor. */}
          <div className="shrink-0 w-9" />
          <SortHeader field="serial_number"  sort={sort} onClick={toggle} className="shrink-0 w-24">{t("ctable.sn", "SN")}</SortHeader>
          <SortHeader field="product_name"   sort={sort} onClick={toggle} className="flex-1 min-w-0">{t("ctable.product", "Product")}</SortHeader>
          <SortHeader field="hours"          sort={sort} onClick={toggle} className="shrink-0 w-16 text-right block">{t("ctable.hours", "Hours")}</SortHeader>
          <SortHeader field="date_delivered" sort={sort} onClick={toggle} className="shrink-0 w-20 text-right block">{t("ctable.delivered", "Delivered")}</SortHeader>
          <SortHeader field="invoice_number" sort={sort} onClick={toggle} className="shrink-0 w-20 text-right block">{t("ctable.invoice", "Invoice")}</SortHeader>
          {/* PIN column slot (only used when the compressor has a PIN
              type). Stays on the right since PIN is a much rarer
              affordance than PM. */}
          <div className="shrink-0 w-12" />
        </div>
        {sortedItems.map((p, idx) => {
          // PIN system is available only when PinType is set AND > 0.
          // (PinType IS NULL / 0 means the product doesn't use this
          // PIN lock.)
          const pinType = Number(p.pin_type);
          const hasPin = p.pin_type != null && pinType > 0;
          return (
          <div key={p.id} className={`min-w-[700px] flex items-center gap-2 px-2 py-1.5 text-[11px] ${idx % 2 === 0 ? "bg-white" : (isCA ? "bg-red-50/40" : "bg-blue-50/40")} border-b last:border-b-0 ${isCA ? "border-red-100" : "border-blue-100"}`}>
            <button
              type="button"
              data-testid={`customer-compressor-row-${p.serial_number || p.id}`}
              onClick={() => onOpenCompressor && onOpenCompressor(p.serial_id || p.serial_numberID || `${p.serial_number}`)}
              className="flex-1 min-w-0 flex items-center gap-2 text-left hover:bg-slate-50 -mx-2 px-2 py-1 rounded-sm"
              title={t("customer.view_detail", "View detail")}
            >
              <div className="shrink-0 w-10 text-[10px] font-bold text-slate-500 truncate">
                {p.product_type ? p.product_type.slice(0, 4).toUpperCase() : "—"}
              </div>
              {/* iter150AZ.53 — PM button rendered IMMEDIATELY before
                  the serial number so it visually reads as part of
                  the SN identity. Span instead of button (it lives
                  inside the row's outer <button>); click handler
                  stops propagation and opens the PM viewer. */}
              <span
                role="button"
                tabIndex={0}
                data-testid={`customer-compressor-pm-${p.serial_number || p.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPmFor({
                    serial_id: p.serial_id || p.serial_numberID || p.serial_number,
                    serial_number: p.serial_number,
                    product_name: p.product_name,
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setPmFor({
                      serial_id: p.serial_id || p.serial_numberID || p.serial_number,
                      serial_number: p.serial_number,
                      product_name: p.product_name,
                    });
                  }
                }}
                title={t("customer.pm_title",
                         "View PM schedule: purchased parts, missing parts, next service")}
                className="shrink-0 w-9 inline-flex items-center justify-center gap-0.5 px-1 py-0.5 text-[9px] uppercase font-bold tracking-wider rounded-sm text-emerald-700 hover:bg-emerald-50 border border-emerald-200 cursor-pointer"
              >
                <Wrench size={10} weight="bold" />
                PM
              </span>
              <div className="shrink-0 w-24 font-mono font-semibold text-slate-800 truncate">
                {p.serial_number || "—"}
              </div>
              <div className="flex-1 min-w-0 text-slate-700 truncate">
                {p.product_name || "—"}
              </div>
              <div className="shrink-0 w-16 text-right font-mono text-slate-600 text-[10px]">
                {p.hours != null ? `${Number(p.hours).toLocaleString()} h` : "—"}
              </div>
              <div className="shrink-0 w-20 text-right text-slate-500 text-[10px]">
                {p.date_delivered ? fmtDate(p.date_delivered) : "—"}
              </div>
              <div className="shrink-0 w-20 text-right text-slate-500 text-[10px] truncate">
                {p.invoice_number ? `#${p.invoice_number}` : (p.invoice_date ? fmtDate(p.invoice_date) : "—")}
              </div>
            </button>
            {/* PIN button stays on the right since PIN-locked
                compressors are rare and the column slot is reserved. */}
            <div className="shrink-0 w-12 flex items-center justify-end gap-1">
              {hasPin && (
                <button
                  type="button"
                  data-testid={`customer-compressor-pin-${p.serial_number || p.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPinFor({
                      serial_number: p.serial_number,
                      product_name: p.product_name,
                      pin_type: pinType,
                    });
                  }}
                  title={t("customer.pin_title", "Generate response PIN for this compressor (type {{n}})", { n: pinType })}
                  className="inline-flex items-center justify-center gap-0.5 px-1.5 py-1 text-[9px] uppercase font-bold tracking-wider rounded-sm text-amber-700 hover:bg-amber-50 border border-amber-200">
                  <Key size={10} weight="bold" />
                  PIN
                </button>
              )}
            </div>
          </div>
          );
        })}
      </div>
      {pinFor && (
        <PinGeneratorDialog
          serialNumber={pinFor.serial_number}
          productName={pinFor.product_name}
          pinType={pinFor.pin_type}
          onClose={() => setPinFor(null)}
        />
      )}
      {/* iter150AZ.32 — PM schedule modal. */}
      {pmFor && (
        <CompressorPmViewerModal
          open
          serialId={pmFor.serial_id}
          serialNumber={pmFor.serial_number}
          productName={pmFor.product_name}
          onClose={() => setPmFor(null)}
        />
      )}
    </CollapsibleSection>
  );
}

function PartsPurchasedSection({ items, ccy, isCA, onOpenInvoice }) {
  const t = useT();
  // iter150AZ.37 — Type (q_type) and Kind (Product / PM / Part /
  // Warranty) groupings. User-toggleable via the toolbar.
  // Default groupBy = "kind" so reps see Product vs PM vs Part vs
  // Warranty groups out of the box, sorted desc by invoice date
  // within each group.
  const [groupBy, setGroupBy] = useState("kind"); // "kind" | "type" | "none"

  const enriched = useMemo(() => {
    return (items || []).map((p) => {
      const isFinal = (p.product_type || "").toLowerCase() === "product";
      const isPm = Number(p.is_pm_part) === 1 || p.is_pm_part === true;
      const qt = (p.q_type || "Sales");
      const kind = isFinal ? "Product" : isPm ? "PM Part" : "Part";
      // Warranty / Return rows overlay the kind column with a tag —
      // for ``groupBy=type`` they bucket under their q_type instead.
      const type = qt;
      return { ...p, _kind: kind, _type: type };
    });
  }, [items]);

  // Sortable row ordering. Default: invoice_date desc.
  const { rows: sortedRows, sort, toggle } = useSortable(
    enriched, "invoice_date", "desc",
  );

  // Bucket rows by either kind or type — or render as a single
  // flat list when groupBy = "none".
  const grouped = useMemo(() => {
    if (groupBy === "none") return [{ key: "", label: "", rows: sortedRows }];
    const orderKind = ["Product", "PM Part", "Part"];
    const orderType = ["Sales", "Warranty", "Return"];
    const order = groupBy === "kind" ? orderKind : orderType;
    const byKey = new Map();
    sortedRows.forEach((r) => {
      const k = groupBy === "kind" ? r._kind : r._type;
      if (!byKey.has(k)) byKey.set(k, []);
      byKey.get(k).push(r);
    });
    const known = order
      .filter((k) => byKey.has(k))
      .map((k) => ({ key: k, label: k, rows: byKey.get(k) }));
    // Any unexpected/null keys at the end.
    const extras = [...byKey.keys()]
      .filter((k) => !order.includes(k))
      .map((k) => ({ key: String(k || "Other"), label: String(k || "Other"), rows: byKey.get(k) }));
    return [...known, ...extras];
  }, [sortedRows, groupBy]);

  if (!items || items.length === 0) return null;

  const groupBtn = (key, label) => {
    const active = groupBy === key;
    return (
      <button
        key={key}
        type="button"
        onClick={(e) => { e.stopPropagation(); setGroupBy(key); }}
        data-testid={`customer-parts-groupby-${key}`}
        className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm transition ${
          active
            ? (isCA ? "bg-red-700 text-white" : "bg-blue-700 text-white")
            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <CollapsibleSection
      testid="customer-parts-section"
      title={t("customer.parts_purchased_title", "Parts & Products Purchased")}
      count={items.length}
      icon={FileIcon}
      headerExtra={({ open }) => (
        // iter150AZ.53 — Hide the "Group: Kind | Type | Flat" bar
        // when the section is collapsed. Keeps the closed header
        // visually clean and prevents the toolbar from looking like
        // a duplicate of itself when another section is expanded.
        open ? (
          <div className="shrink-0 flex items-center gap-1" data-testid="customer-parts-groupby-bar">
            <span className="text-[9px] uppercase tracking-wider text-slate-400 mr-0.5">Group:</span>
            {groupBtn("kind", "Kind")}
            {groupBtn("type", "Type")}
            {groupBtn("none", "Flat")}
          </div>
        ) : null
      )}
    >
      <div className={`rounded-md border ${isCA ? "border-red-100" : "border-blue-100"} overflow-hidden`}>
        {/* Sortable header — click any column label to sort. */}
        <div className={`min-w-[700px] sticky top-0 z-10 flex items-center gap-2 px-2 py-1 border-b ${isCA ? "bg-red-50 border-red-100" : "bg-blue-50 border-blue-100"}`}>
          <SortHeader field="invoice_date"    sort={sort} onClick={toggle} className="shrink-0 w-16">{t("ctable.date", "Date")}</SortHeader>
          <SortHeader field="_type"           sort={sort} onClick={toggle} className="shrink-0 w-24">{t("ctable.type", "Type")}</SortHeader>
          <SortHeader field="_kind"           sort={sort} onClick={toggle} className="shrink-0 w-20">{t("ctable.kind", "Kind")}</SortHeader>
          <SortHeader field="invoice_number"  sort={sort} onClick={toggle} className="shrink-0 w-24">{t("ctable.invoice", "Invoice")}</SortHeader>
          <SortHeader field="product_name"    sort={sort} onClick={toggle} className="flex-1 min-w-0">{t("ctable.part", "Part")}</SortHeader>
          <SortHeader field="quantity"        sort={sort} onClick={toggle} className="shrink-0 w-12 text-right block">{t("ctable.qty", "Qty")}</SortHeader>
          <SortHeader field="price"           sort={sort} onClick={toggle} className="shrink-0 w-20 text-right block">{t("ctable.price", "Price")}</SortHeader>
        </div>
        {grouped.map((grp) => (
          <div key={grp.key || "_all"} data-testid={`customer-parts-group-${grp.key || "all"}`}>
            {groupBy !== "none" && (
              <div className={`min-w-[700px] flex items-center gap-2 px-2 py-1 text-[10px] font-bold border-b ${isCA ? "bg-red-100/40 border-red-200" : "bg-blue-100/40 border-blue-200"}`}>
                <span className="text-slate-700">{grp.label}</span>
                <span className="text-slate-400 normal-case font-normal tracking-normal">({grp.rows.length})</span>
              </div>
            )}
            {grp.rows.map((p, idx) => {
              const qt = p._type;
              const qtClass =
                qt === "Warranty" ? "bg-amber-100 text-amber-800 border-amber-200"
                : qt === "Return" ? "bg-rose-100 text-rose-800 border-rose-200"
                : "bg-slate-100 text-slate-600 border-slate-200";
              const kindClass =
                p._kind === "Product" ? "bg-blue-100 text-blue-800 border-blue-200"
                : p._kind === "PM Part" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                : "bg-slate-100 text-slate-700 border-slate-200";
              return (
                <button
                  key={p.ID}
                  type="button"
                  data-testid={`customer-part-row-${p.ID}`}
                  onClick={() => p.heading_id && onOpenInvoice && onOpenInvoice(p.heading_id)}
                  className={`w-full min-w-[700px] flex items-center gap-2 px-2 py-1.5 text-[11px] ${idx % 2 === 0 ? "bg-white" : (isCA ? "bg-red-50/40" : "bg-blue-50/40")} hover:bg-slate-50 border-b last:border-b-0 ${isCA ? "border-red-100" : "border-blue-100"} text-left`}
                >
                  <div className="shrink-0 w-16 text-[10px] text-slate-500">
                    {p.invoice_date ? fmtDate(p.invoice_date) : "—"}
                  </div>
                  <div className="shrink-0 w-24">
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm border ${qtClass}`}>
                      {qt}
                    </span>
                  </div>
                  <div className="shrink-0 w-20">
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm border ${kindClass}`}>
                      {p._kind}
                    </span>
                  </div>
                  <div className="shrink-0 w-24 font-mono text-slate-700 text-[10px] truncate">
                    {p.invoice_number ? `#${p.invoice_number}` : (p.heading_id ? "—" : "—")}
                  </div>
                  <div className="flex-1 min-w-0 text-slate-800 font-medium truncate">
                    {p.product_name || p.product_code || "—"}
                  </div>
                  <div className="shrink-0 w-12 text-right font-mono text-slate-600 text-[10px]">
                    {Number(p.quantity || 0).toLocaleString()}
                  </div>
                  <div className="shrink-0 w-20 text-right font-mono text-slate-600 text-[10px]">
                    {fmtMoney(p.price, ccy)}
                  </div>
                </button>
              );
            })}
          </div>
        ))}
        {sortedRows.length === 0 && (
          <div className="p-3 text-center text-[11px] text-slate-400 italic">
            {t("customer.parts_tab_empty", "No items.")}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

function JournalSection({ items, ccy, isCA }) {
  const t = useT();
  const navigate = useNavigate();
  // iter150AZ.37 — sortable headers. Default: entry_date desc.
  const { rows, sort, toggle } = useSortable(items, "entry_date", "desc");
  if (!items || items.length === 0) return null;
  const totalCredit = items.reduce((s, e) => s + (Number(e.credit) || 0), 0);
  const totalDebit  = items.reduce((s, e) => s + (Number(e.debit)  || 0), 0);
  return (
    <CollapsibleSection
      testid="customer-journal-section"
      title="Journal Entries"
      count={items.length}
      icon={FileText}
      headerExtra={(
        <span className="text-[10px] font-mono text-slate-500 shrink-0">
          <span className="text-green-700">+{fmtMoney(totalCredit, ccy)}</span> /
          <span className="text-red-600 ml-1">-{fmtMoney(totalDebit, ccy)}</span>
        </span>
      )}
    >
      <div className={`rounded-md border ${isCA ? "border-red-100" : "border-blue-100"} overflow-x-auto max-h-72 overflow-y-auto`}>
        {/* Sortable header */}
        <div className={`min-w-[700px] sticky top-0 z-10 flex items-center gap-2 px-2 py-1 border-b ${isCA ? "bg-red-50 border-red-100" : "bg-blue-50 border-blue-100"}`}>
          <SortHeader field="entry_date"     sort={sort} onClick={toggle} className="shrink-0 w-16">{t("ctable.date", "Date")}</SortHeader>
          <SortHeader field="entry_type"     sort={sort} onClick={toggle} className="flex-1 min-w-0">{t("ctable.type_desc", "Type / Description")}</SortHeader>
          <SortHeader field="credit"         sort={sort} onClick={toggle} className="shrink-0 w-20 text-right block">{t("ctable.credit", "Credit")}</SortHeader>
          <SortHeader field="debit"          sort={sort} onClick={toggle} className="shrink-0 w-20 text-right block">{t("ctable.debit", "Debit")}</SortHeader>
        </div>
        {rows.map((j, idx) => {
          // Each entry's `quote` field IS the QuoteInvoiceHeadingID — link to /quote/:id.
          const quoteId = (j.quote || j.heading_id || "").toString().trim();
          const clickable = !!quoteId;
          return (
            <button
              key={j.journal_id}
              type="button"
              data-testid={`customer-journal-row-${j.journal_id}`}
              onClick={() => clickable && navigate(`/quote/${encodeURIComponent(quoteId)}`)}
              disabled={!clickable}
              title={clickable ? `Open quote/invoice ${quoteId}` : "No linked quote"}
              className={`w-full min-w-[700px] flex items-center gap-2 px-2 py-1.5 text-[11px] ${idx % 2 === 0 ? "bg-white" : (isCA ? "bg-red-50/40" : "bg-blue-50/40")} ${clickable ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"} border-b last:border-b-0 ${isCA ? "border-red-100" : "border-blue-100"} text-left`}
            >
              <div className="shrink-0 w-16 text-[10px] text-slate-500">{fmtDate(j.entry_date)}</div>
              <div className="flex-1 min-w-0 flex items-center gap-1.5">
                <span className="text-slate-700 font-medium truncate">{j.entry_type || "—"}</span>
                {j.status && j.status !== "N/A" && <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-200">{j.status}</span>}
                {j.invoice_number && <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-blue-50 text-blue-700 border border-blue-200">#{j.invoice_number}</span>}
                {j.explanation && <span className="text-[10px] text-slate-400 truncate">· {j.explanation}</span>}
              </div>
              <div className="shrink-0 w-20 text-right font-mono text-[10px]">
                {Number(j.credit) > 0 ? <span className="text-green-700">+{fmtMoney(j.credit, ccy)}</span> : "—"}
              </div>
              <div className="shrink-0 w-20 text-right font-mono text-[10px]">
                {Number(j.debit) > 0 ? <span className="text-red-600">-{fmtMoney(j.debit, ccy)}</span> : "—"}
              </div>
            </button>
          );
        })}
      </div>
    </CollapsibleSection>
  );
}

/**
 * PhoneLogsSection (iter149e) — recent phone calls tied to a customer
 * via PhoneLogs.customerID. Uses the same compact row pattern as the
 * Journal section so the customer detail panel keeps a consistent feel.
 */
function PhoneLogsSection({ items, isCA }) {
  const t = useT();
  const [openId, setOpenId] = useState(null);
  // iter150AZ.37 — sortable. Default: call_at desc.
  const { rows, sort, toggle } = useSortable(items, "call_at", "desc");
  if (!items || items.length === 0) return null;

  const fmtCallDate = (s) => {
    if (!s) return "—";
    try {
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return String(s).slice(0, 16).replace("T", " ");
      return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return String(s).slice(0, 16).replace("T", " "); }
  };
  const fmtDur = (s) => {
    const n = Number(s);
    if (!n || n < 0) return "—";
    const m = Math.floor(n / 60); const sec = n % 60;
    return m > 0 ? `${m}m ${sec.toString().padStart(2, "0")}s` : `${sec}s`;
  };
  const directionTone = (d) =>
    (d || "").toLowerCase().includes("out")
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-blue-50 text-blue-700 border-blue-200";

  return (
    <CollapsibleSection
      testid="customer-phone-logs-section"
      title="Phone Logs"
      count={items.length}
      icon={Phone}
    >
      <div className={`rounded-md border ${isCA ? "border-red-100" : "border-blue-100"} overflow-x-auto max-h-72 overflow-y-auto`}>
        {/* Sortable header */}
        <div className={`min-w-[700px] sticky top-0 z-10 flex items-center gap-2 px-2 py-1 border-b ${isCA ? "bg-red-50 border-red-100" : "bg-blue-50 border-blue-100"}`}>
          <SortHeader field="call_at"   sort={sort} onClick={toggle} className="shrink-0 w-24">{t("ctable.when", "When")}</SortHeader>
          <SortHeader field="direction" sort={sort} onClick={toggle} className="shrink-0 w-12">{t("ctable.dir", "Dir")}</SortHeader>
          <SortHeader field="from_num"  sort={sort} onClick={toggle} className="flex-1 min-w-0">{t("ctable.party_summary", "Party / Summary")}</SortHeader>
          <SortHeader field="duration"  sort={sort} onClick={toggle} className="shrink-0 w-16 text-right block">{t("ctable.duration", "Duration")}</SortHeader>
          <div className="shrink-0 w-4" />
        </div>
        {rows.map((row, idx) => {
          const id = row.id || row.call_sid || `pl-${idx}`;
          const isOpen = openId === id;
          const direction = row.direction || (row.call_type || "").toLowerCase();
          const otherParty = direction.toLowerCase().includes("out") ? row.to_num : row.from_num;
          return (
            <div key={id}
                 data-testid={`customer-phone-log-row-${id}`}
                 className={`text-[11px] ${idx % 2 === 0 ? "bg-white" : (isCA ? "bg-red-50/40" : "bg-blue-50/40")} border-b last:border-b-0 ${isCA ? "border-red-100" : "border-blue-100"}`}>
              <button type="button" onClick={() => setOpenId(isOpen ? null : id)}
                      className="w-full min-w-[700px] flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 text-left">
                <div className="shrink-0 w-24 text-[10px] text-slate-500 truncate">{fmtCallDate(row.call_at)}</div>
                <span className={`shrink-0 w-12 text-center text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm border ${directionTone(direction)}`}>
                  {(direction || row.call_type || "call").slice(0, 3)}
                </span>
                <div className="flex-1 min-w-0 flex items-center gap-1.5">
                  <span className="font-mono text-slate-700 truncate">{otherParty || row.from_num || "—"}</span>
                  {row.caller_name && <span className="text-slate-500 truncate">· {row.caller_name}</span>}
                  {row.status && (
                    <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm border ${
                      (row.status || "").toLowerCase() === "completed"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}>
                      {row.status}
                    </span>
                  )}
                  {row.summary && <span className="text-[10px] text-slate-400 truncate">· {row.summary}</span>}
                </div>
                <div className="shrink-0 w-16 text-right text-[10px] font-mono text-slate-500">{fmtDur(row.duration)}</div>
                <CaretDown size={10} className={`shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div data-testid={`customer-phone-log-detail-${id}`}
                     className="px-3 pb-3 pt-1 text-[11px] text-slate-700 space-y-1.5 bg-slate-50 border-t border-slate-100">
                  {row.handled_by && <div><b>Handled by:</b> {row.handled_by}</div>}
                  {row.reason && <div><b>Reason:</b> {row.reason}</div>}
                  {row.outcome && <div><b>Outcome:</b> {row.outcome}</div>}
                  {row.sentiment && <div><b>Sentiment:</b> {row.sentiment}</div>}
                  {row.transcript && (
                    <details className="mt-1">
                      <summary className="text-indigo-700 cursor-pointer text-[10px] font-bold uppercase tracking-wider">Transcript</summary>
                      <pre className="whitespace-pre-wrap font-sans mt-1 p-2 bg-white border border-slate-200 rounded-sm text-[10px] leading-relaxed max-h-40 overflow-auto">{row.transcript}</pre>
                    </details>
                  )}
                  {row.recording_url && (
                    <audio controls preload="none" className="w-full mt-1" src={row.recording_url}>
                      Your browser does not support audio playback.
                    </audio>
                  )}
                  {row.related_sn && (
                    <div className="text-[10px] text-slate-500">Related SN: <span className="font-mono">{row.related_sn}</span></div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </CollapsibleSection>
  );
}


function PeopleSection({ persons, loading, customerId, companyName, isCA, user, onChanged }) {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [videoTarget, setVideoTarget] = useState(null);    // { personId, personName }
  const [smsTarget, setSmsTarget] = useState(null);        // { personId, personName, personPhone }

  const submitNew = async (form) => {
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${encodeURIComponent(customerId)}/persons`,
        form,
        { withCredentials: true, headers: user?.email ? { "X-User-Email": user.email } : {} }
      );
      setAdding(false);
      onChanged && onChanged();
    } catch (e) { setError(e?.response?.data?.detail || e?.message || "Failed to save"); }
  };
  const submitEdit = async (pfcid, form) => {
    setError(""); setBusyId(pfcid);
    try {
      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${encodeURIComponent(customerId)}/persons/${encodeURIComponent(pfcid)}`,
        form,
        { withCredentials: true, headers: user?.email ? { "X-User-Email": user.email } : {} }
      );
      setEditingId(null);
      onChanged && onChanged();
    } catch (e) { setError(e?.response?.data?.detail || e?.message || "Failed to save"); }
    finally { setBusyId(null); }
  };
  const removeContact = async (p) => {
    if (!window.confirm(`Delete ${p.Name}? This cannot be undone.`)) return;
    setError(""); setBusyId(p.PFCID);
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${encodeURIComponent(customerId)}/persons/${encodeURIComponent(p.PFCID)}`,
        { withCredentials: true, headers: user?.email ? { "X-User-Email": user.email } : {} }
      );
      onChanged && onChanged();
    } catch (e) { setError(e?.response?.data?.detail || e?.message || "Failed to delete"); }
    finally { setBusyId(null); }
  };

  const addBtn = customerId ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setAdding((x) => !x); setEditingId(null); setError(""); }}
      data-testid="customer-add-person-btn"
      className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border ${isCA ? "border-red-200 text-red-700 hover:bg-red-50" : "border-blue-200 text-blue-700 hover:bg-blue-50"}`}
    >
      {adding ? "Cancel" : "+ Add contact"}
    </button>
  ) : null;

  return (
    <CollapsibleSection
      testid="customer-people-section"
      title="People"
      count={persons?.length || 0}
      icon={User}
      headerExtra={addBtn}
    >
      {error && <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-sm p-2 mb-2" data-testid="customer-people-error">{error}</div>}
      {adding && <PersonForm isCA={isCA} initial={{}} onCancel={() => setAdding(false)} onSubmit={submitNew} testIdPrefix="add-person" />}
      {loading ? (
        <div className="flex items-center gap-2 py-2 text-xs text-slate-400"><Spinner size={14} className="animate-spin" /> Loading contacts...</div>
      ) : (!persons || persons.length === 0) ? (
        !adding && <p className="text-xs text-slate-400">{t("customer.no_contacts", "No contacts found")}</p>
      ) : (
        <div className="space-y-2">
          {persons.map((p) => editingId === p.PFCID ? (
            <PersonForm key={p.id} isCA={isCA} initial={p} onCancel={() => setEditingId(null)} onSubmit={(form) => submitEdit(p.PFCID, form)} testIdPrefix={`edit-person-${p.id}`} busy={busyId === p.PFCID} />
          ) : (
            <PersonRow key={p.id} p={p} isCA={isCA} companyName={companyName}
              customerId={customerId}
              onEdit={() => { setEditingId(p.PFCID); setAdding(false); setError(""); }}
              onDelete={() => removeContact(p)}
              onVideo={() => setVideoTarget({ personId: p.PFCID, personName: p.Name })}
              onSms={() => setSmsTarget({
                personId: p.PFCID, personName: p.Name,
                personPhone: p.Telephone || p.Telephone_Alternate || "",
              })}
              busy={busyId === p.PFCID}
            />
          ))}
        </div>
      )}
      <VideoCallModal
        open={!!videoTarget}
        onClose={() => setVideoTarget(null)}
        customerId={customerId}
        customerName={companyName}
        personName={videoTarget?.personName || ""}
        userEmail={user?.email}
      />
      <SmsChatPanel
        open={!!smsTarget}
        onClose={() => setSmsTarget(null)}
        customerId={customerId}
        customerName={companyName}
        personId={smsTarget?.personId}
        personName={smsTarget?.personName || ""}
        personPhone={smsTarget?.personPhone || ""}
      />
    </CollapsibleSection>
  );
}

function PersonRow({ p, isCA, companyName, customerId, onEdit, onDelete, onVideo, onSms, busy }) {
  const t = useT();
  const callHref = toTelHref(p.Telephone, p.Extension) || toTelHref(p.Telephone_Alternate);
  const emailHref = p["Main Email"]
    ? `mailto:${p["Main Email"]}?subject=${encodeURIComponent(`Re: ${companyName || ""}`.trim())}`
    : null;
  const hasPhone = !!(p.Telephone || p.Telephone_Alternate);
  const [sendFilesOpen, setSendFilesOpen] = useState(false);
  const personEmail = (p["Main Email"] || "").trim();
  return (
    <div className={`rounded-sm border p-2.5 ${isCA ? "border-red-100 bg-red-50/30" : "border-blue-100 bg-blue-50/30"} ${busy ? "opacity-60" : ""}`}>
      {/* Header: name + tag chips. Wraps to next line if too many tags. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1.5">
        <span className="text-sm font-semibold text-slate-900 mr-1">{p.Name}</span>
        {p.IsPrimary === "Y" && (
          <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm ${isCA ? "text-[#D80621] bg-red-100" : "text-blue-600 bg-blue-100"}`}>Primary</span>
        )}
        {p.Purchasing === "Y" && <span className="text-[9px] font-medium text-slate-500 bg-slate-100 px-1 py-0.5 rounded-sm">Purchasing</span>}
        {p.Accounting === "Y" && <span className="text-[9px] font-medium text-slate-500 bg-slate-100 px-1 py-0.5 rounded-sm">Accounting</span>}
        {p.Service === "Y" && <span className="text-[9px] font-medium text-slate-500 bg-slate-100 px-1 py-0.5 rounded-sm">Service</span>}
      </div>
      {/* Action toolbar: separate row so it never gets clipped on the right. */}
      <div className="flex flex-wrap items-center gap-1 mb-1.5">
        {callHref && (
          <a href={callHref} onClick={(e) => e.stopPropagation()} data-testid={`person-call-btn-${p.id}`}
            className={`text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-6 rounded-sm border ${isCA ? "border-red-300 text-red-700 hover:bg-red-100" : "border-blue-300 text-blue-700 hover:bg-blue-100"}`}
            title={`Call ${p.Telephone || p.Telephone_Alternate || ""}`}>
            <Phone size={11} weight="fill" /> Call
          </a>
        )}
        {hasPhone && onSms && (
          <button onClick={(e) => { e.stopPropagation(); onSms(); }} data-testid={`person-sms-btn-${p.id}`}
            className="text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-6 rounded-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            title={`Send SMS to ${p.Name}`}>
            <ChatCircle size={11} weight="fill" /> Text
          </button>
        )}
        {onVideo && (
          <button onClick={(e) => { e.stopPropagation(); onVideo(); }} data-testid={`person-video-btn-${p.id}`}
            className="text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-6 rounded-sm border border-violet-300 text-violet-700 hover:bg-violet-50"
            title={`Start video call with ${p.Name}`}>
            <VideoCamera size={11} weight="fill" /> Video
          </button>
        )}
        {emailHref && (
          <a href={emailHref} onClick={(e) => e.stopPropagation()} data-testid={`person-email-btn-${p.id}`}
            className={`text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-6 rounded-sm border ${isCA ? "border-red-300 text-red-700 hover:bg-red-100" : "border-blue-300 text-blue-700 hover:bg-blue-100"}`}
            title={`Email ${p["Main Email"]}`}>
            <Envelope size={11} weight="fill" /> Email
          </a>
        )}
        {p["Main Email"] && (
          <ResendManualButton email={p["Main Email"]} name={p.Name} isCA={isCA} />
        )}
        {personEmail && (
          <button
            onClick={(e) => { e.stopPropagation(); setSendFilesOpen(true); }}
            data-testid={`person-send-files-btn-${p.id}`}
            title={`Send ACC / US Air documents to ${p.Name}`}
            className="text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-6 rounded-sm bg-blue-700 text-white hover:bg-blue-800"
          >
            <PaperPlaneTilt size={11} weight="fill" /> Docs
          </button>
        )}
        {p.PFCID && (
          <>
            <span className="ml-auto" />
            <button onClick={onEdit} data-testid={`person-edit-btn-${p.id}`}
              className="text-[10px] font-bold uppercase tracking-wider px-2 h-6 rounded-sm border border-slate-200 text-slate-500 hover:bg-slate-50">
              {t("customer.person_edit", "Edit")}
            </button>
            <button onClick={onDelete} data-testid={`person-delete-btn-${p.id}`} disabled={busy}
              className="text-[10px] font-bold uppercase tracking-wider px-2 h-6 rounded-sm border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50">
              {t("customer.person_delete", "Delete")}
            </button>
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        {p["Main Email"] && (
          <a href={emailHref} className="inline-flex items-center gap-1 text-blue-700 underline underline-offset-2 break-all">
            <Envelope size={12} weight="fill" className="text-blue-600 shrink-0" />{p["Main Email"]}
          </a>
        )}
        {p.Telephone && (
          <a href={toTelHref(p.Telephone, p.Extension)} className="inline-flex items-center gap-1 text-blue-700 underline underline-offset-2">
            <Phone size={12} weight="fill" className="text-blue-600" />{p.Telephone}{p.Extension ? ` x${p.Extension}` : ""}
          </a>
        )}
        {p.Telephone_Alternate && (
          <a href={toTelHref(p.Telephone_Alternate)} className="inline-flex items-center gap-1 text-blue-700 underline underline-offset-2">
            <Phone size={12} weight="fill" className="text-blue-600" />{p.Telephone_Alternate}
          </a>
        )}
      </div>
      <SendPublicFilesModal
        open={sendFilesOpen}
        onClose={() => setSendFilesOpen(false)}
        customerId={customerId}
        recipients={personEmail ? [personEmail] : []}
        title={`Send documents to ${p.Name}`}
      />
    </div>
  );
}

function ResendManualButton({ email, name, isCA }) {
  const [sending, setSending] = useState(false);
  const send = async (e) => {
    e.stopPropagation();
    if (!email) return;
    setSending(true);
    try {
      const lang = (navigator.language || "en").slice(0, 2).toLowerCase();
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/manual/share`, {
        email, role: "customer",
        lang: ["en", "fr", "es"].includes(lang) ? lang : "en",
        message: "",
        sender_name: "",
      }, { withCredentials: true });
      toast.success(`Manual sent to ${name || email}`, { description: email });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed";
      toast.error(typeof detail === "string" ? detail : "Failed to send manual");
    } finally {
      setSending(false);
    }
  };
  return (
    <button onClick={send} disabled={sending}
      data-testid={`person-manual-btn-${email}`}
      className={`text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-6 rounded-sm border disabled:opacity-50 ${
        isCA ? "border-red-300 text-red-700 hover:bg-red-100" : "border-violet-300 text-violet-700 hover:bg-violet-50"
      }`}
      title={`Resend the user manual to ${email}`}>
      {sending ? <Spinner size={11} className="animate-spin" /> : <BookOpen size={11} weight="fill" />} Manual
    </button>
  );
}


function PersonForm({ isCA, initial, onCancel, onSubmit, testIdPrefix, busy }) {
  const t = useT();
  const [form, setForm] = useState({
    name: initial?.Name || "",
    email: initial?.["Main Email"] || "",
    phone: initial?.Telephone || "",
    telephone_alternate: initial?.Telephone_Alternate || "",
    extension: initial?.Extension || "",
    is_primary: initial?.IsPrimary === "Y",
    purchasing: initial?.Purchasing === "Y",
    accounting: initial?.Accounting === "Y",
    service: initial?.Service === "Y",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e) => { e.preventDefault(); onSubmit(form); };
  const accentBtn = isCA ? "bg-red-700 hover:bg-red-800" : "bg-blue-700 hover:bg-blue-800";
  const tint = isCA ? "border-red-200 bg-red-50/40" : "border-blue-200 bg-blue-50/40";
  return (
    <form onSubmit={submit} className={`rounded-sm border p-3 mb-2 ${tint}`} data-testid={`${testIdPrefix}-form`}>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("customer.person_full_name_placeholder", "Full name")} required maxLength={120} data-testid={`${testIdPrefix}-name`} className="text-xs p-1.5 rounded-sm border border-slate-200 bg-white outline-none col-span-2" />
        <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" maxLength={200} data-testid={`${testIdPrefix}-email`} className="text-xs p-1.5 rounded-sm border border-slate-200 bg-white outline-none" />
        <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone" maxLength={80} data-testid={`${testIdPrefix}-phone`} className="text-xs p-1.5 rounded-sm border border-slate-200 bg-white outline-none" />
        <input type="tel" value={form.telephone_alternate} onChange={(e) => set("telephone_alternate", e.target.value)} placeholder={t("customer.person_alt_phone_placeholder", "Alt phone (optional)")} maxLength={80} data-testid={`${testIdPrefix}-altphone`} className="text-xs p-1.5 rounded-sm border border-slate-200 bg-white outline-none" />
        <input value={form.extension} onChange={(e) => set("extension", e.target.value)} placeholder="Ext." maxLength={8} data-testid={`${testIdPrefix}-ext`} className="text-xs p-1.5 rounded-sm border border-slate-200 bg-white outline-none" />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600 mb-2">
        {[
          ["is_primary", "Primary"],
          ["purchasing", "Purchasing"],
          ["accounting", "Accounting"],
          ["service", "Service"],
        ].map(([k, label]) => (
          <label key={k} className="inline-flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`${testIdPrefix}-${k}`} /> {label}
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm border border-slate-200 text-slate-500 hover:bg-white">{t("customer.modal_cancel", "Cancel")}</button>
        <button type="submit" disabled={busy || !form.name.trim()} data-testid={`${testIdPrefix}-save`} className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm text-white ${busy ? "bg-slate-400" : accentBtn} disabled:opacity-60`}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}


/**
 * iter150AZ.37 — Sortable Notes & Files table.
 */
function NotesTable({ items, isCA }) {
  const t = useT();
  const mapValue = (n, field) => {
    if (field === "_kind")    return n.kind === "file" ? (n.Type || "FILE") : (n.Action || "NOTE");
    if (field === "_text")    return n.ai_explanation || n.Explanation || "";
    return n[field];
  };
  const { rows, sort, toggle } = useSortable(items, "date_created", "desc", mapValue);
  return (
    <div className={`rounded-md border ${isCA ? "border-red-100" : "border-blue-100"} overflow-x-auto max-h-72 overflow-y-auto`}>
      <div className={`min-w-[700px] sticky top-0 z-10 flex items-center gap-2 px-2 py-1 border-b ${isCA ? "bg-red-50 border-red-100" : "bg-blue-50 border-blue-100"}`}>
        <SortHeader field="_kind"        sort={sort} onClick={toggle} className="shrink-0 w-12">{t("ctable.kind", "Kind")}</SortHeader>
        <SortHeader field="_text"        sort={sort} onClick={toggle} className="flex-1 min-w-0">{t("ctable.text", "Text")}</SortHeader>
        <SortHeader field="created_by"   sort={sort} onClick={toggle} className="shrink-0 w-24 text-right block">{t("ctable.by", "By")}</SortHeader>
        <SortHeader field="date_created" sort={sort} onClick={toggle} className="shrink-0 w-16 text-right block">{t("ctable.date", "Date")}</SortHeader>
      </div>
      {rows.map((n, i) => (
        <div
          key={`${n.kind}-${n.id || i}`}
          data-testid={`customer-note-row-${n.id || i}`}
          className={`min-w-[700px] flex items-center gap-2 px-2 py-1.5 text-[11px] ${i % 2 === 0 ? "bg-white" : (isCA ? "bg-red-50/40" : "bg-blue-50/40")} border-b last:border-b-0 ${isCA ? "border-red-100" : "border-blue-100"}`}
          title={[n.Explanation, n.ai_explanation].filter(Boolean).join(" · ") || ""}
        >
          <div className="shrink-0 w-12 flex items-center gap-0.5">
            <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm border ${n.kind === "file" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {n.kind === "file" ? (n.Type || "FILE") : (n.Action || "NOTE")}
            </span>
            {n.ai_explanation && (
              <span className="text-[9px] font-bold text-violet-700" title="AI-polished">✨</span>
            )}
          </div>
          <div className="flex-1 min-w-0 text-slate-700 truncate">
            {n.ai_explanation || n.Explanation || "—"}
          </div>
          <div className="shrink-0 w-24 text-right text-[10px] text-slate-500 truncate">
            {n.created_by || "—"}
          </div>
          <div className="shrink-0 w-16 text-right text-[10px] text-slate-500">
            {n.date_created ? fmtDate(n.date_created) : "—"}
          </div>
        </div>
      ))}
    </div>
  );
}


function NotesListSection({ items, isCA, customerId, onAddNote }) {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try { await onAddNote(text.trim()); setText(""); setAdding(false); }
    finally { setSaving(false); }
  };
  const addBtn = customerId ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setAdding((x) => !x); }}
      data-testid="customer-add-note-btn"
      className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border ${isCA ? "border-red-200 text-red-700 hover:bg-red-50" : "border-blue-200 text-blue-700 hover:bg-blue-50"}`}
    >
      {adding ? "Cancel" : "+ Add note"}
    </button>
  ) : null;
  return (
    <CollapsibleSection
      testid="customer-notes-list-section"
      title="Notes & Files"
      count={items?.length || 0}
      icon={FileText}
      headerExtra={addBtn}
    >
      {adding && (
        <div className={`mb-2 p-2 rounded-md border ${isCA ? "border-red-200 bg-red-50/30" : "border-blue-200 bg-blue-50/30"}`}>
          <textarea
            value={text} onChange={(e) => setText(e.target.value)}
            placeholder={t("customer.note_placeholder", "Quick note — AI will polish it for the CRM record…")}
            className="w-full text-xs p-2 rounded-sm border border-slate-200 bg-white outline-none resize-none min-h-[60px]"
            data-testid="customer-add-note-textarea"
            disabled={saving}
          />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => { setAdding(false); setText(""); }} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border border-slate-200 text-slate-500 hover:bg-slate-50" disabled={saving}>{t("customer.modal_cancel", "Cancel")}</button>
            <button onClick={submit} disabled={!text.trim() || saving} className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm ${saving ? "bg-slate-300 text-white" : (isCA ? "bg-red-700 text-white" : "bg-blue-700 text-white")}`} data-testid="customer-add-note-save">
              {saving ? "Saving…" : "Save + AI polish ✨"}
            </button>
          </div>
        </div>
      )}
      {(!items || items.length === 0) ? (
        !adding && <p className="text-[11px] text-slate-400 italic">{t("customer.no_notes", "No notes recorded yet.")}</p>
      ) : (
        <NotesTable items={items} isCA={isCA} />
      )}
    </CollapsibleSection>
  );
}

/**
 * iter150AZ.41 — Two-field address section. Bill-to = invoice address;
 * shipping = where parts/compressors actually ship. Admins and members
 * of the handling company (same gate as Create Quote) can edit either
 * field inline. The PATCH endpoint mirrors the canonical values back,
 * which we propagate to the parent via ``onSaved``.
 */
function AddressesSection({ customerId, billTo, shipTo, canEdit, onSaved }) {
  const t = useT();
  const [editing, setEditing] = useState(null); // "bill" | "ship" | null
  const [draftBill, setDraftBill] = useState(billTo || "");
  const [draftShip, setDraftShip] = useState(shipTo || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setDraftBill(billTo || ""); }, [billTo]);
  useEffect(() => { setDraftShip(shipTo || ""); }, [shipTo]);

  const beginEdit = (which) => {
    setError("");
    setDraftBill(billTo || "");
    setDraftShip(shipTo || "");
    setEditing(which);
  };
  const cancel = () => { setEditing(null); setError(""); };
  const save = async (which) => {
    if (!customerId || saving) return;
    setSaving(true);
    setError("");
    try {
      const body = which === "bill"
        ? { bill_to_address: draftBill }
        : { shipping_address: draftShip };
      const r = await api.patch(
        `/customers/${encodeURIComponent(customerId)}/addresses`,
        body,
      );
      onSaved && onSaved(r?.data?.bill_to_address, r?.data?.shipping_address);
      setEditing(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const Row = ({ which, label, value, draft, setDraft, testid }) => {
    const isEditing = editing === which;
    return (
      <div className="py-2 border-b border-slate-100 last:border-b-0" data-testid={`addresses-row-${which}`}>
        <div className="flex items-start gap-2">
          <MapPin size={14} className="text-slate-400 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
              {canEdit && !isEditing && (
                <button
                  type="button"
                  data-testid={`addresses-edit-${which}`}
                  onClick={() => beginEdit(which)}
                  className="text-[10px] font-bold uppercase tracking-wider text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {value ? t("action.edit", "Edit") : t("action.add", "Add")}
                </button>
              )}
            </div>
            {isEditing ? (
              <div className="mt-1 space-y-1">
                <textarea
                  data-testid={`addresses-input-${which}`}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={3}
                  className="w-full text-sm text-slate-800 border border-slate-300 rounded-sm px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  placeholder={t("customer.address_placeholder", "Street, City, State/Province, Postal, Country")}
                />
                {error && <div className="text-[10px] text-rose-700" data-testid={`addresses-error-${which}`}>{error}</div>}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid={`addresses-save-${which}`}
                    disabled={saving}
                    onClick={() => save(which)}
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {saving ? t("action.saving", "Saving…") : t("action.save", "Save")}
                  </button>
                  <button
                    type="button"
                    data-testid={`addresses-cancel-${which}`}
                    onClick={cancel}
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-slate-300 text-slate-600 hover:bg-slate-50"
                  >
                    {t("action.cancel", "Cancel")}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid={testid}>
                {value || <span className="text-slate-300 italic">{t("customer.no_address", "Not provided")}</span>}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <DetailSection title={t("customer.addresses_title", "Addresses")} compact>
      <Row which="bill" label={t("customer.detail_bill_to_address", "Bill to Address")} value={billTo} draft={draftBill} setDraft={setDraftBill} testid="addresses-bill-value" />
      <Row which="ship" label={t("customer.detail_shipping_address", "Shipping Address")} value={shipTo} draft={draftShip} setDraft={setDraftShip} testid="addresses-ship-value" />
    </DetailSection>
  );
}



function DetailRow({ icon, label, value, multiline, compact }) {
  if (!value || value === "None") return null;
  if (compact && !multiline) {
    return (
      <div className="flex items-baseline gap-2 py-1 border-b border-slate-100 last:border-b-0">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 shrink-0 min-w-[100px]">{label}</span>
        <span className="text-sm text-slate-800 truncate">{String(value)}</span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2">
      {icon && <span className="text-slate-400 mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <p className={`text-sm text-slate-800 ${multiline ? "" : "truncate"}`}>{String(value)}</p>
      </div>
    </div>
  );
}

function statusBadgeClass(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("sold") || s.includes("confirmed")) return "text-green-700 bg-green-100 border-green-200";
  if (s === "quote" || s === "estimate" || s === "preliminary") return "text-amber-700 bg-amber-100 border-amber-200";
  if (s.includes("cancel")) return "text-slate-500 bg-slate-100 border-slate-200 line-through";
  if (s.includes("return") || s.includes("warranty")) return "text-purple-700 bg-purple-100 border-purple-200";
  if (s.includes("arrived")) return "text-blue-700 bg-blue-100 border-blue-200";
  return "text-slate-600 bg-slate-100 border-slate-200";
}

function formatQuoteDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }); }
  catch { return String(d); }
}

/**
 * iter150AZ.37 — Sortable table inside QuotesSection. Click any column
 * header to sort. Default sort: invoice_date desc, falling back to
 * quote_date desc for un-invoiced rows. The "SOLD" tag is shown only
 * when an invoice number exists AND the status indicates a confirmed
 * sale — matches the user's spec: "if sold and order confirmed and
 * there is an invoice number, only show it as sold when expanded".
 */
function QuotesTable({ quotes, isCA, onOpen }) {
  const t = useT();
  // Use ``invoice_date`` if present, else ``quote_date`` so the date
  // column sorts consistently.
  const mapValue = (q, field) => {
    if (field === "_date")     return q.invoice_date || q.quote_date;
    if (field === "_type")     return q.invoice_number ? "INV" : "QUO";
    if (field === "_unit")     return Number(q.line_subtotal || 0);
    if (field === "_disc")     return Number(q.total_discount_pct || 0);
    return q[field];
  };
  const { rows: sorted, sort, toggle } = useSortable(
    quotes, "_date", "desc", mapValue,
  );
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className={`rounded-md border ${isCA ? "border-red-100" : "border-blue-100"} overflow-x-auto max-h-96 overflow-y-auto`}>
      <div className={`min-w-[700px] sticky top-0 z-10 flex items-center gap-2 px-2 py-1 border-b ${isCA ? "bg-red-50 border-red-100" : "bg-blue-50 border-blue-100"}`}>
        <SortHeader field="_date"          sort={sort} onClick={toggle} className="shrink-0 w-20">{t("ctable.date", "Date")}</SortHeader>
        <SortHeader field="_type"          sort={sort} onClick={toggle} className="shrink-0 w-12">{t("ctable.type", "Type")}</SortHeader>
        <SortHeader field="Status"         sort={sort} onClick={toggle} className="shrink-0 w-24">{t("ctable.status", "Status")}</SortHeader>
        <SortHeader field="invoice_number" sort={sort} onClick={toggle} className="shrink-0 w-24">{t("ctable.invoice_hash", "Invoice #")}</SortHeader>
        <SortHeader field="line_qty"       sort={sort} onClick={toggle} className="shrink-0 w-12 text-right block">{t("ctable.qty", "Qty")}</SortHeader>
        <SortHeader field="_unit"          sort={sort} onClick={toggle} className="shrink-0 w-20 text-right block">{t("ctable.unit_price", "Unit Price")}</SortHeader>
        <SortHeader field="_disc"          sort={sort} onClick={toggle} className="shrink-0 w-14 text-right block">{t("ctable.disc_pct", "Disc %")}</SortHeader>
        <SortHeader field="total"          sort={sort} onClick={toggle} className="shrink-0 w-24 text-right block">{t("ctable.total", "Total")}</SortHeader>
        <SortHeader field="po_number"      sort={sort} onClick={toggle} className="flex-1 min-w-0">{t("ctable.po_hash", "PO #")}</SortHeader>
        <SortHeader field="prepared_by"    sort={sort} onClick={toggle} className="shrink-0 w-20">{t("ctable.by", "By")}</SortHeader>
      </div>
      {sorted.map((q) => {
        const isInvoice = !!q.invoice_number;
        const statusLc = (q.Status || "").toLowerCase();
        const isSoldConfirmed = isInvoice && (statusLc.includes("sold") || statusLc.includes("order confirmed"));
        const dateLabel = isInvoice ? q.invoice_date : q.quote_date;
        const qty = Number(q.line_qty || 0);
        const subtotal = Number(q.line_subtotal || 0);
        const discountPct = Number(q.total_discount_pct || 0);
        return (
          <div key={q.QuoteInvoiceHeadingID} data-testid={`quote-row-${q.QuoteInvoiceHeadingID}`}>
            <button type="button"
              onClick={() => onOpen(q)}
              className={`w-full min-w-[700px] flex items-center gap-2 px-2 py-1.5 text-[11px] text-left transition-colors hover:bg-slate-50 border-b last:border-b-0 ${isCA ? "border-red-100" : "border-blue-100"}`}>
              <div className="shrink-0 w-20 text-slate-500 text-[10px]">
                {formatQuoteDate(dateLabel)}
              </div>
              <div className="shrink-0 w-12 text-[9px] font-bold text-slate-500 flex items-center gap-0.5">
                {isInvoice ? <FileText size={11} weight="fill" className="text-blue-600" /> : <Receipt size={11} weight="bold" className="text-amber-600" />}
                <span>{isInvoice ? "INV" : "QUO"}</span>
              </div>
              <div className="shrink-0 w-24 flex items-center gap-1">
                <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm border ${statusBadgeClass(q.Status)}`}>
                  {q.Status || "—"}
                </span>
                {isSoldConfirmed && (
                  <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-green-100 text-green-800 border border-green-200"
                        data-testid={`quote-sold-tag-${q.QuoteInvoiceHeadingID}`}>
                    SOLD
                  </span>
                )}
              </div>
              <div className="shrink-0 w-24 font-mono font-semibold text-slate-800 truncate">
                {isInvoice ? `#${q.invoice_number}` : q.QuoteInvoiceHeadingID.split("__")[0]}
              </div>
              <div className="shrink-0 w-12 text-right font-mono text-slate-600 text-[10px]">
                {qty > 0 ? qty.toLocaleString() : "—"}
              </div>
              <div className="shrink-0 w-20 text-right font-mono text-slate-600 text-[10px]">
                {subtotal > 0 ? `$${subtotal.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}` : "—"}
              </div>
              <div className="shrink-0 w-14 text-right font-mono text-[10px]">
                {discountPct > 0
                  ? <span className="text-rose-700">-{discountPct}%</span>
                  : <span className="text-slate-300">—</span>}
              </div>
              <div className="shrink-0 w-24 text-right font-mono font-bold text-slate-900 text-[11px]">
                {q.total != null ? `$${Number(q.total).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}` : "—"}
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-1.5 text-[10px]">
                {q.po_number ? (
                  <>
                    <span className="truncate text-slate-700 font-medium">{q.po_number}</span>
                    <a
                      href={`${apiBase}/api/quotes/${encodeURIComponent(q.QuoteInvoiceHeadingID)}/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`quote-po-download-${q.QuoteInvoiceHeadingID}`}
                      onClick={(e) => e.stopPropagation()}
                      title="Download PO PDF"
                      className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    >
                      ↓ PO
                    </a>
                  </>
                ) : (
                  <span className="text-slate-300">—</span>
                )}
              </div>
              <div className="shrink-0 w-20 text-[10px] text-slate-500 truncate">
                {q.prepared_by ? String(q.prepared_by).split("@")[0] : "—"}
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}


function QuotesSection({ quotes, loading, isCA, user, onQuoteMutated }) {
  const t = useT();
  const [openQuote, setOpenQuote] = useState(null);
  const count = quotes.length;
  return (
    <CollapsibleSection
      testid="customer-quotes-section"
      title="Quotes & Invoices"
      count={count}
      icon={Receipt}
    >
      {loading ? (
        <div className="flex items-center gap-2 py-2 text-xs text-slate-400"><Spinner size={14} className="animate-spin" /> Loading quotes...</div>
      ) : count === 0 ? (
        <p className="text-xs text-slate-400">{t("customer.no_quotes", "No quotes or invoices found")}</p>
      ) : (
        <QuotesTable quotes={quotes} isCA={isCA} onOpen={setOpenQuote} />
      )}
      {openQuote && <QuoteDetailModal headingId={openQuote.QuoteInvoiceHeadingID} onClose={() => { setOpenQuote(null); onQuoteMutated && onQuoteMutated(); }} user={user} />}
    </CollapsibleSection>
  );
}

const SUPER_EDITORS_FE = SUPER_EDITOR_EMAILS;
const QUOTE_STATUS_OPTIONS = [
  "Quote", "Preliminary", "Estimate", "Sold & Order Confirmed", "Alternative Sold",
  "Warranty", "Return Ticket", "Goods Arrived", "Cancelled"
];

function QuoteDetailModal({ headingId, onClose, user }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingField, setSavingField] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [toast, setToast] = useState(null);
  const [filesFor, setFilesFor] = useState(null); // { ID, "Product Name", _ComputedKey }
  const audience = user?.role === "customer" ? "customer" : "all";

  const reload = useCallback(() => {
    return fetchQuote(headingId)
      .then((d) => { setData(d); setError(null); return d; })
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load quote"));
  }, [headingId]);

  useEffect(() => {
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [reload]);

  const h = data?.heading;
  const isInvoice = !!h?.invoice_number;
  const userEmail = (user?.email || "").toLowerCase();
  const isSuper = SUPER_EDITORS_FE.includes(userEmail);
  const isPreparer = userEmail && userEmail === (h?.prepared_by || "").toLowerCase();
  const canEdit = !!h && !isInvoice && (user?.is_admin || isSuper || isPreparer);

  const showError = (msg) => {
    setToast({ kind: "error", msg });
    setTimeout(() => setToast(null), 4000);
  };

  const saveHeading = async (patch) => {
    const key = Object.keys(patch)[0];
    setSavingField(key);
    try {
      await updateQuoteHeading(headingId, patch);
      await reload();
    } catch (e) {
      showError(e?.response?.data?.detail || "Save failed — see admin about DB permissions");
    } finally {
      setSavingField(null);
    }
  };

  const saveLine = async (detailId, patch) => {
    setSavingField(`line:${detailId}`);
    try {
      await updateQuoteLine(headingId, detailId, patch);
      await reload();
    } catch (e) {
      showError(e?.response?.data?.detail || "Line update failed — check DB permissions");
    } finally {
      setSavingField(null);
    }
  };

  const removeLine = async (detailId) => {
    if (!window.confirm("Delete this line item?")) return;
    setDeletingId(detailId);
    try {
      await deleteQuoteLine(headingId, detailId);
      await reload();
    } catch (e) {
      showError(e?.response?.data?.detail || "Delete failed — DB may lack DELETE permission");
    } finally {
      setDeletingId(null);
    }
  };

  const addLine = async (line) => {
    try {
      await addQuoteLine(headingId, line);
      await reload();
      setShowAdd(false);
    } catch (e) {
      showError(e?.response?.data?.detail || "Add failed");
    }
  };

  const title = loading
    ? "Loading..."
    : isInvoice
      ? `Invoice #${h.invoice_number}`
      : `Quote ${headingId.split("__")[0]}`;

  const subtotal = (data?.details || []).reduce(
    (s, d) => s + (Number(d.price) || 0) * (Number(d.Quantity) || 0) * (1 - (Number(d.discount) || 0)),
    0
  );

  return createPortal(
    <div className="fixed inset-0 z-[60]" data-testid="quote-detail-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 top-8 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[820px] md:max-h-[90vh] md:rounded-sm bg-white md:border md:border-slate-300 rounded-t-xl shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-2 min-w-0">
            {isInvoice ? <FileText size={18} weight="fill" className="text-blue-600 shrink-0" /> : <Receipt size={18} weight="bold" className="text-amber-600 shrink-0" />}
            <h3 className="text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">{title}</h3>
            {h?.Status && !canEdit && (
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${statusBadgeClass(h.Status)}`}>{h.Status}</span>
            )}
            {isInvoice && <span className="text-[10px] font-bold uppercase text-slate-400">{t("customer.invoice_locked", "Invoice · locked")}</span>}
          </div>
          <button onClick={onClose} data-testid="quote-detail-close" className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-500 hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        {toast && (
          <div className={`sticky top-[52px] z-10 px-4 py-2 text-xs font-semibold ${toast.kind === "error" ? "bg-red-50 text-red-700 border-b border-red-200" : "bg-green-50 text-green-700"}`} data-testid="quote-toast">
            {toast.msg}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20"><Spinner size={24} className="animate-spin text-slate-400" /></div>
        ) : error ? (
          <div className="p-6 text-center text-red-600 text-sm">{error}</div>
        ) : !h ? null : (
          <div className="p-4 md:p-5 space-y-4">
            {/* Heading grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <HeadingField label={t("customer.quote_quote_date", "Quote Date")} value={formatQuoteDate(h.quote_date)} />
              {h.invoice_date && <HeadingField label={t("customer.quote_invoice_date", "Invoice Date")} value={formatQuoteDate(h.invoice_date)} />}
              {h.Q_Type && <HeadingField label="Type" value={h.Q_Type} />}
              {h.prepared_by && <HeadingField label={t("customer.quote_prepared_by", "Prepared By")} value={String(h.prepared_by).split("@")[0]} />}
              {canEdit ? (
                <EditableField label="Status" value={h.Status} onSave={(v) => saveHeading({ Status: v })} saving={savingField === "Status"} options={QUOTE_STATUS_OPTIONS} testid="edit-status" />
              ) : h.Status ? <HeadingField label="Status" value={h.Status} /> : null}
              {canEdit ? (
                <EditableField label={t("customer.quote_po_number", "PO Number")} value={h.po_number || ""} onSave={(v) => saveHeading({ po_number: v })} saving={savingField === "po_number"} testid="edit-po" />
              ) : h.po_number ? <HeadingField label={t("customer.quote_po_number", "PO Number")} value={h.po_number} /> : null}
              {canEdit ? (
                <EditableField label={t("customer.quote_validity_days", "Validity (days)")} value={h.Validity ?? ""} type="number" onSave={(v) => saveHeading({ Validity: Number(v) || 0 })} saving={savingField === "Validity"} testid="edit-validity" />
              ) : h.Validity ? <HeadingField label={t("customer.quote_valid_for", "Valid for")} value={`${h.Validity} days`} /> : null}
              {!isInvoice && h.validity_ends && <HeadingField label="Expires" value={formatQuoteDate(h.validity_ends)} />}
              {canEdit ? (
                <EditableField label="Payment" value={h.Payment || ""} onSave={(v) => saveHeading({ Payment: v })} saving={savingField === "Payment"} testid="edit-payment" />
              ) : h.Payment ? <HeadingField label="Payment" value={h.Payment} /> : null}
            </div>

            {/* Address */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">{t("customer.quote_ship_to", "Ship To")}</p>
              {canEdit ? (
                <EditableTextarea value={h.address || ""} onSave={(v) => saveHeading({ address: v })} saving={savingField === "address"} testid="edit-address" placeholder="Address..." />
              ) : (
                <p className="text-sm text-slate-700 whitespace-pre-line">{h.address || "—"}</p>
              )}
            </div>

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("customer.quote_line_items", "Line Items")} ({data.detail_count})</p>
                {canEdit && !showAdd && (
                  <button onClick={() => setShowAdd(true)} data-testid="quote-add-line-btn"
                    className="text-[11px] font-semibold text-blue-700 hover:bg-blue-50 px-2 py-0.5 rounded-sm border border-blue-200">
                    {t("customer.quote_add_line_item", "+ Add line item")}
                  </button>
                )}
              </div>
              {showAdd && <AddLineForm onAdd={addLine} onCancel={() => setShowAdd(false)} />}
              <div className="border border-slate-200 rounded-sm overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 z-10 bg-slate-50 shadow-[0_1px_0_0_rgb(226_232_240)]">
                    <tr className="text-left border-b border-slate-200">
                      <th className="px-2 py-1.5 font-semibold uppercase tracking-wider text-slate-500 text-[10px] bg-slate-50">#</th>
                      <th className="px-2 py-1.5 font-semibold uppercase tracking-wider text-slate-500 text-[10px] bg-slate-50">{t("table.product", "Product")}</th>
                      <th className="px-2 py-1.5 font-semibold uppercase tracking-wider text-slate-500 text-[10px] text-right bg-slate-50">{t("table.qty", "Qty")}</th>
                      <th className="px-2 py-1.5 font-semibold uppercase tracking-wider text-slate-500 text-[10px] text-right bg-slate-50">{t("table.unit_price_short", "Unit $")}</th>
                      <th className="px-2 py-1.5 font-semibold uppercase tracking-wider text-slate-500 text-[10px] text-right bg-slate-50">{t("table.disc_pct", "Disc %")}</th>
                      <th className="px-2 py-1.5 font-semibold uppercase tracking-wider text-slate-500 text-[10px] text-right bg-slate-50">{t("table.line_total_short", "Line $")}</th>
                      {canEdit && <th className="px-1 py-1.5 w-7 bg-slate-50" />}
                    </tr>
                  </thead>
                  <tbody>
                    {data.details.map((d, idx) => {
                      const qty = Number(d.Quantity) || 0;
                      const price = Number(d.price) || 0;
                      const disc = Number(d.discount) || 0;
                      const line = qty * price * (1 - disc);
                      const isSaving = savingField === `line:${d.QuoteInvoiceDetailID}`;
                      const isDeleting = deletingId === d.QuoteInvoiceDetailID;
                      return (
                        <tr key={d.QuoteInvoiceDetailID} data-testid={`quote-line-${d.QuoteInvoiceDetailID}`} className={`border-b border-slate-100 ${idx % 2 ? "bg-slate-50/50" : "bg-white"} ${isSaving || isDeleting ? "opacity-50" : ""}`}>
                          <td className="px-2 py-1.5 text-slate-400 font-mono text-[10px]">{idx + 1}</td>
                          <td className="px-2 py-1.5">
                            <div className="flex items-start gap-1.5">
                              {d.part_id && (
                                <button data-testid={`quote-line-files-${d.QuoteInvoiceDetailID}`}
                                  onClick={() => setFilesFor({ ID: d.part_id, "Product Name": d.product_name, _ComputedKey: d.product_code })}
                                  title={t("customer.related_files", "Related files")}
                                  className="mt-0.5 w-5 h-5 inline-flex items-center justify-center rounded-sm border border-slate-200 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 text-slate-500 shrink-0">
                                  <FileIcon size={11} />
                                </button>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-slate-900">{d.product_name || "—"}</div>
                                {d.explanation && <div className="text-[10px] text-slate-500 mt-0.5">{d.explanation}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="px-1 py-1 text-right">
                            {canEdit ? (
                              <NumCell value={qty} step="1" min="0" onSave={(v) => saveLine(d.QuoteInvoiceDetailID, { quantity: Number(v) })} />
                            ) : <span className="font-mono">{qty}</span>}
                          </td>
                          <td className="px-1 py-1 text-right">
                            {canEdit ? (
                              <NumCell value={price} step="0.01" min="0" prefix="$" onSave={(v) => saveLine(d.QuoteInvoiceDetailID, { price: Number(v) })} />
                            ) : <span className="font-mono">${price.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>}
                          </td>
                          <td className="px-1 py-1 text-right">
                            {canEdit ? (
                              <NumCell value={(disc * 100).toFixed(1)} step="0.5" min="0" max="100" onSave={(v) => saveLine(d.QuoteInvoiceDetailID, { discount: Math.max(0, Math.min(1, Number(v) / 100)) })} />
                            ) : <span className="font-mono text-slate-500">{disc > 0 ? `${(disc * 100).toFixed(1)}%` : "—"}</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono font-semibold">${line.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          {canEdit && (
                            <td className="px-1 py-1 text-center">
                              <button onClick={() => removeLine(d.QuoteInvoiceDetailID)} data-testid={`delete-line-${d.QuoteInvoiceDetailID}`}
                                className="w-6 h-6 inline-flex items-center justify-center rounded-sm text-slate-400 hover:bg-red-50 hover:text-red-600">
                                <X size={13} weight="bold" />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {data.details.length === 0 && (
                      <tr><td colSpan={canEdit ? 7 : 6} className="px-2 py-4 text-center text-slate-400">{t("customer.no_line_items", "No line items.")} {canEdit && t("customer.click_add_line", "Click + Add line item to start.")}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-full md:w-72 text-xs space-y-1">
                <TotalRow label="Subtotal" value={subtotal} />
                {canEdit ? (
                  <InlineTotalRow label="Shipping" value={h.Shipping} onSave={(v) => saveHeading({ Shipping: Number(v) })} saving={savingField === "Shipping"} />
                ) : h.Shipping > 0 && <TotalRow label="Shipping" value={h.Shipping} />}
                {canEdit ? (
                  <InlineTotalRow label={t("customer.quote_sales_tax", "Sales Tax")} value={h.sales_tax} onSave={(v) => saveHeading({ sales_tax: Number(v) })} saving={savingField === "sales_tax"} />
                ) : h.sales_tax > 0 && <TotalRow label={t("customer.quote_sales_tax", "Sales Tax")} value={h.sales_tax} />}
                {canEdit ? (
                  <InlineTotalRow label="Start-Up" value={h.start_up_charge} onSave={(v) => saveHeading({ start_up_charge: Number(v) })} saving={savingField === "start_up_charge"} />
                ) : h.start_up_charge > 0 && <TotalRow label="Start-Up" value={h.start_up_charge} />}
                <div className="flex justify-between border-t-2 border-slate-300 pt-1.5 mt-1">
                  <span className="font-bold text-slate-900 uppercase tracking-wide">Total</span>
                  <span className="font-mono font-bold text-lg text-slate-900">
                    ${Number(h.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            {/* Special Instructions */}
            <div className="border-t border-slate-100 pt-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">{t("customer.quote_special_instructions", "Special Instructions")}</p>
              {canEdit ? (
                <EditableTextarea value={h.special_instructions || ""} onSave={(v) => saveHeading({ special_instructions: v })} saving={savingField === "special_instructions"} testid="edit-special" placeholder="Notes..." />
              ) : (
                <p className="text-xs text-slate-600 whitespace-pre-line">{h.special_instructions || "—"}</p>
              )}
            </div>
          </div>
        )}
      </div>
      {filesFor && <PartFilesModal part={filesFor} onClose={() => setFilesFor(null)} audience={audience} />}
    </div>,
    document.body
  );
}

function EditableField({ label, value, onSave, saving, options, type = "text", testid }) {
  const [draft, setDraft] = useState(value ?? "");
  const [focused, setFocused] = useState(false);
  useEffect(() => { setDraft(value ?? ""); }, [value]);
  const commit = () => {
    if (String(draft) !== String(value ?? "")) onSave(draft);
    setFocused(false);
  };
  const baseClass = "w-full text-sm text-slate-800 font-medium bg-white border rounded-sm px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500";
  return (
    <div>
      <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
        {label} {saving && <Spinner size={10} className="animate-spin text-blue-500" />}
      </p>
      {options ? (
        <select data-testid={testid} value={draft} onChange={(e) => { setDraft(e.target.value); onSave(e.target.value); }}
          className={`${baseClass} border-slate-300 focus:border-blue-500`}>
          {!options.includes(draft) && draft && <option value={draft}>{draft}</option>}
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input data-testid={testid} type={type} value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } if (e.key === "Escape") { setDraft(value ?? ""); e.currentTarget.blur(); } }}
          className={`${baseClass} ${focused ? "border-blue-500" : "border-slate-200 hover:border-slate-300"}`} />
      )}
    </div>
  );
}

function EditableTextarea({ value, onSave, saving, testid, placeholder }) {
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const commit = () => { if ((draft || "") !== (value || "")) onSave(draft); };
  return (
    <div className="relative">
      <textarea data-testid={testid} value={draft} placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        className="w-full text-sm text-slate-700 bg-white border border-slate-200 rounded-sm px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 hover:border-slate-300 resize-y"
        rows={Math.min(6, Math.max(2, (draft || "").split("\n").length))} />
      {saving && <Spinner size={12} className="animate-spin text-blue-500 absolute top-2 right-2" />}
    </div>
  );
}

function NumCell({ value, step, min, max, prefix, onSave }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  const commit = () => { if (draft !== String(value)) onSave(draft); };
  return (
    <div className="relative inline-block">
      {prefix && <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">{prefix}</span>}
      <input type="number" value={draft} step={step} min={min} max={max}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setDraft(String(value)); e.currentTarget.blur(); } }}
        className={`w-20 text-right font-mono text-xs bg-white border border-slate-200 rounded-sm py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 hover:border-slate-300 ${prefix ? "pl-4 pr-1.5" : "px-1.5"}`} />
    </div>
  );
}

function InlineTotalRow({ label, value, onSave, saving }) {
  const [draft, setDraft] = useState(String(value || 0));
  useEffect(() => { setDraft(String(value || 0)); }, [value]);
  const commit = () => { if (Number(draft) !== Number(value || 0)) onSave(Number(draft) || 0); };
  return (
    <div className="flex justify-between items-center text-slate-600">
      <span className="flex items-center gap-1">{label} {saving && <Spinner size={10} className="animate-spin text-blue-500" />}</span>
      <div className="relative">
        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">$</span>
        <input type="number" step="0.01" min="0" value={draft}
          onChange={(e) => setDraft(e.target.value)} onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          className="w-24 text-right font-mono text-xs pl-4 pr-1.5 py-0.5 bg-white border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 hover:border-slate-300" />
      </div>
    </div>
  );
}

function AddLineForm({ onAdd, onCancel }) {
  const t = useT();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");
  const [disc, setDisc] = useState("0");
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.length < 2 || selected) { setResults([]); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      searchParts(query, 15)
        .then(d => setResults(d.results || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, selected]);

  const pick = (r) => {
    setSelected(r);
    setQuery(r.name);
    if (r.price_usd) setPrice(String(r.price_usd));
    setResults([]);
  };

  const submit = () => {
    if (!selected || !qty || Number(qty) <= 0) return;
    onAdd({
      product_code: selected.code,
      quantity: Number(qty),
      price: Number(price) || 0,
      discount: (Number(disc) || 0) / 100,
    });
  };

  return (
    <div className="mb-2 p-3 border-2 border-dashed border-blue-200 rounded-sm bg-blue-50/30 space-y-2" data-testid="add-line-form">
      <div className="relative">
        <input type="text" placeholder={t("customer.parts_search_placeholder", "Search parts by name or code (min 2 chars)...")} value={query}
          onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
          data-testid="add-line-search"
          className="w-full text-sm px-2 py-1.5 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400" autoFocus />
        {searching && <Spinner size={12} className="animate-spin absolute right-2 top-2 text-slate-400" />}
        {results.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-300 rounded-sm shadow-lg max-h-56 overflow-y-auto">
            {results.map((r) => (
              <button key={r.code} type="button" onClick={() => pick(r)}
                className="w-full text-left px-2 py-1 text-xs hover:bg-blue-50 border-b border-slate-100 last:border-b-0 flex justify-between items-center gap-2">
                <span className="font-semibold text-slate-800 truncate">{r.name}</span>
                {r.price_usd != null && <span className="shrink-0 font-mono text-[11px] text-green-700">${Number(r.price_usd).toFixed(2)}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      {selected && (
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-[9px] uppercase font-bold text-slate-500 block">Qty</label>
            <input type="number" min="0" step="1" value={qty} onChange={(e) => setQty(e.target.value)}
              className="w-20 px-1.5 py-1 text-sm border border-slate-300 rounded-sm font-mono" data-testid="add-line-qty" />
          </div>
          <div>
            <label className="text-[9px] uppercase font-bold text-slate-500 block">Unit $</label>
            <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
              className="w-24 px-1.5 py-1 text-sm border border-slate-300 rounded-sm font-mono" data-testid="add-line-price" />
          </div>
          <div>
            <label className="text-[9px] uppercase font-bold text-slate-500 block">Disc %</label>
            <input type="number" min="0" max="100" step="0.5" value={disc} onChange={(e) => setDisc(e.target.value)}
              className="w-20 px-1.5 py-1 text-sm border border-slate-300 rounded-sm font-mono" data-testid="add-line-disc" />
          </div>
          <div className="flex gap-1 ml-auto">
            <button onClick={onCancel} className="px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 rounded-sm">{t("customer.modal_cancel", "Cancel")}</button>
            <button onClick={submit} data-testid="add-line-submit"
              className="px-3 py-1 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-sm">{t("customer.parts_modal_add", "Add")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function HeadingField({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm text-slate-800 font-medium truncate">{String(value)}</p>
    </div>
  );
}

function TotalRow({ label, value }) {
  return (
    <div className="flex justify-between text-slate-600">
      <span>{label}</span>
      <span className="font-mono">${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
    </div>
  );
}
